import React, { useState, useEffect, useRef, useMemo } from 'react';
import { generateVideoPrompt, transcribeDialogueAudio } from '../services/geminiService';
import { generateLipsyncWithPixverse } from '../services/falService';
import { generateSpeechWithElevenLabs } from '../services/elevenlabsService';
import { getImageToVideoModels, getStartEndFrameVideoModels, getAllVideoModels, generateVideoProductionPrompt, generateSpeech } from '../services/aiService';
import { uploadDataUriToBackend } from '../services/backendService';
import { backfillByteplusForCharacters } from '../services/actorsService';
import { getResolutionOptionsForModel, getDefaultResolutionForModel, getResolutionCreditsForModel } from '../utils/videoModelResolutions';
import { getVideoDurationProfile, applyModelDurationProfile } from '../utils/videoModelDurations';
import { getCharacterCostumeForVideo, ensureHostedCostumeUrls, buildAudioReferencesPayload, buildByteplusAssetsPayload, buildProductReferencesPayload } from '../services/videoPayloadBuilder';
import { CinematicPromptEditorModal } from './CinematicPromptEditorModal';
import { ReferenceVideosPanel, buildReferenceVideosPayload } from './ReferenceVideosPanel';
import { LoadingSpinner } from './icons/LoadingSpinner';
import { CloseIcon } from './icons/CloseIcon';
import { MagicWandIcon } from './icons/MagicWandIcon';
import { DownloadIcon } from './icons/DownloadIcon';
import { SaveIcon } from './icons/SaveIcon';
import { MicrophoneIcon } from './icons/MicrophoneIcon';
import { StopCircleIcon } from './icons/StopCircleIcon';
import { TrashIcon } from './icons/TrashIcon';
import { PlusIcon } from './icons/PlusIcon';
import { v4 as uuidv4 } from 'uuid';
import { ElevenLabsVoiceModal } from './ElevenLabsVoiceModal';
import { CREDIT_COSTS } from '../types';
import { parsePanelsFromDescription } from '../utils/panelParser';
import { extractSceneActionsFromPanels } from '../utils/panelSceneActionsExtractor';

// Fallback models if backend returns empty (based on common video generation models)
const DEFAULT_VIDEO_MODELS = [
    { id: 'kling-2.5-turbo', name: 'Kling 2.5 Turbo', provider: 'Kling', disabled: false },
    { id: 'wan-2.2', name: 'Wan 2.2', provider: 'Wan', disabled: false },
    { id: 'gemini-3-fast-preview', name: 'Gemini 3 Fast Preview', provider: 'Google', disabled: false },
];

const CAMERA_SHOTS = [
    'Static Shot', 'Cinematic Pan Left', 'Cinematic Pan Right', 'Tilt Up', 'Tilt Down',
    'Zoom In', 'Zoom Out', 'Dolly Zoom', 'Crane Shot', 'Aerial Shot', 'Handheld Shot'
];

const FONT_OPTIONS = [
    'Cinematic', 'Serif', 'Sans-serif', 'Handwriting', 'Futuristic', 'Horror', 'Elegant'
];

const Switch = ({ checked, onChange }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`${checked ? 'bg-purple-600' : 'bg-gray-600'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
    >
        <span className={`${checked ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
    </button>
);

const TabButton = ({ name, tabId, activeTab, setActiveTab }) => (
    <button
        onClick={() => setActiveTab(tabId)}
        className={`flex-1 py-3 text-center text-sm font-semibold transition-colors ${activeTab === tabId ? 'bg-purple-600/20 text-purple-300 border-b-2 border-purple-400' : 'text-gray-400 hover:bg-gray-700/50'}`}
    >
        {name}
    </button>
);

/**
 * Flatten the admin backend's `scene_actions[]` array into the same
 * { id, startTime, endTime, action } shape we use for `timedActions`.
 *
 * Schema (admin response):
 *   scene_actions: [
 *     {
 *       start_time: "00:03.5" | "3.5s" | 3.5,
 *       end_time:   "00:07.5" | "7.5s" | 7.5,
 *       action:     "Macro slow push-in on the honeybee landing on a marigold...",
 *       subject_type?: "product" | "environment" | "animal" | "vfx" | "establishing"
 *     }, ...
 *   ]
 *
 * Scene actions cover non-character beats: product hero shots, drone
 * landscapes, animal close-ups, environmental establishers, VFX sequences.
 * They merge alongside `character_actions[]` rows on the same timeline.
 *
 * Returns an array of flat rows. Filters out entries with empty action text.
 */
const flattenSceneActions = (rawSceneActions) => {
    if (!Array.isArray(rawSceneActions) || rawSceneActions.length === 0) return [];
    const parseToSec = (v) => {
        if (v === undefined || v === null) return null;
        if (typeof v === 'number') return Number.isFinite(v) ? v : null;
        const s = String(v).trim();
        if (s.includes(':')) {
            const [mm, ss] = s.split(':');
            return (parseInt(mm, 10) || 0) * 60 + (parseFloat(ss) || 0);
        }
        const n = parseFloat(s);
        return Number.isFinite(n) ? n : null;
    };
    return rawSceneActions
        .map((sa) => {
            const text = String(sa?.action || sa?.description || sa?.text || '').trim();
            if (!text) return null;
            const startSec = parseToSec(sa?.start_time ?? sa?.startTime ?? sa?.start);
            const endSec   = parseToSec(sa?.end_time   ?? sa?.endTime   ?? sa?.end);
            if (startSec === null || endSec === null) return null;
            return {
                id: uuidv4(),
                startTime: `${startSec.toFixed(1)}s`,
                endTime:   `${endSec.toFixed(1)}s`,
                action: text,
                subjectType: sa?.subject_type || sa?.subjectType || null,
                source: 'scene',
            };
        })
        .filter(Boolean);
};

const DialogueAudioSection = ({ character, dialogue, onDialogueChange, onGenerateSpeech, generationStatus, generationError, onSelectVoice, estimatedSpeechSec, onDelete, lineIndex }) => {
    const [recordingStatus, setRecordingStatus] = useState('idle');
    const [sourceAudio, setSourceAudio] = useState(null);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [transcriptionError, setTranscriptionError] = useState(null);
    const [recordError, setRecordError] = useState(null);

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    const handleStartRecording = async () => {
        setRecordError(null);
        setSourceAudio(null);
        setTranscriptionError(null);
        onDialogueChange({ clonedAudio: undefined, text: '' });
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];
            
            mediaRecorderRef.current.ondataavailable = event => audioChunksRef.current.push(event.data);
            
            mediaRecorderRef.current.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                stream.getTracks().forEach(track => track.stop());

                if (audioBlob.size === 0) {
                    console.warn("Recorded audio blob is empty.");
                    setRecordingStatus('idle');
                    return;
                }

                const url = URL.createObjectURL(audioBlob);
                setRecordingStatus('recorded');
                
                const reader = new FileReader();
                reader.onloadend = async () => {
                    const base64Data = reader.result;
                    setSourceAudio({
                        url,
                        data: {
                            name: `dialogue-rec-${character.id}-${Date.now()}.webm`,
                            data: base64Data,
                            mimeType: 'audio/webm',
                        }
                    });

                    setIsTranscribing(true);
                    setTranscriptionError(null);
                    try {
                        const transcript = await transcribeDialogueAudio(base64Data);
                        onDialogueChange({ text: transcript });
                    } catch (err) {
                        const msg = err instanceof Error ? err.message : "Transcription failed.";
                        setTranscriptionError(msg);
                    } finally {
                        setIsTranscribing(false);
                    }
                };
                reader.readAsDataURL(audioBlob);
            };
            
            mediaRecorderRef.current.start();
            setRecordingStatus('recording');
        } catch (err) {
            console.error(err);
            setRecordError("Microphone access denied.");
        }
    };

    const handleStopRecording = () => {
        if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
    };
    
    const handleResetRecording = () => {
        if (sourceAudio?.url) {
            URL.revokeObjectURL(sourceAudio.url);
        }
        setSourceAudio(null);
        onDialogueChange({ clonedAudio: undefined });
        setRecordingStatus('idle');
        setRecordError(null);
    };

    useEffect(() => {
        const url = sourceAudio?.url;
        return () => {
            if (url) URL.revokeObjectURL(url);
        };
    }, [sourceAudio]);
    
    const hasClonedVoice = !!character.elevenLabsVoiceId;
    const isProcessing = generationStatus === 'generating' || isTranscribing;

    const displayVoiceName = character.audio?.name || (character.elevenLabsVoiceId ? `ID: ${character.elevenLabsVoiceId.substring(0, 8)}...` : '');

    return (
        <div className="space-y-2 pb-2 border-b border-gray-800 last:border-0">
            <div className="flex items-center gap-2 mb-1">
                <div className="flex items-center gap-1">
                    <input
                        type="text"
                        value={dialogue.startTimecode}
                        onChange={(e) => onDialogueChange({ startTimecode: e.target.value })}
                        placeholder="0.0s"
                        className="w-14 text-xs p-1 bg-gray-700/60 border border-gray-600 rounded-md"
                        disabled={isProcessing}
                        aria-label={`Start timecode for ${character.name}`}
                    />
                    <span className="text-gray-500">-</span>
                    <input
                        type="text"
                        value={dialogue.endTimecode}
                        onChange={(e) => onDialogueChange({ endTimecode: e.target.value })}
                        placeholder="3.0s"
                        className="w-14 text-xs p-1 bg-gray-700/60 border border-gray-600 rounded-md"
                        disabled={isProcessing}
                        aria-label={`End timecode for ${character.name}`}
                    />
                </div>
                <label className="text-xs font-bold text-gray-300 flex-grow truncate">
                    {character.name}{typeof lineIndex === 'number' ? <span className="ml-1 text-gray-500 font-normal">· line {lineIndex + 1}</span> : null}
                </label>
                {recordingStatus === 'idle' && (
                    <button onClick={handleStartRecording} disabled={isProcessing} className="p-1 rounded-full hover:bg-gray-600 disabled:opacity-50">
                        <MicrophoneIcon className="w-4 h-4 text-gray-400 hover:text-white" />
                    </button>
                )}
                 {recordingStatus === 'recording' && (
                    <button onClick={handleStopRecording} className="p-1 rounded-full text-red-400 bg-red-500/20 animate-pulse">
                         <StopCircleIcon className="w-4 h-4" />
                    </button>
                )}
                {onDelete && (
                    <button
                        onClick={onDelete}
                        className="p-1 rounded-full hover:bg-red-500/20 text-red-400 flex-shrink-0"
                        aria-label="Delete this line"
                        title="Delete this line"
                    >
                        <TrashIcon className="w-4 h-4" />
                    </button>
                )}
            </div>
            
            <div className="flex justify-between items-center text-xs text-gray-500 mb-1">
                <span className="truncate max-w-[200px]">
                    {hasClonedVoice ? (
                        <>Voice: <span className="text-green-400 font-mono">{displayVoiceName}</span></>
                    ) : (
                        <span className="text-yellow-500">No voice assigned</span>
                    )}
                </span>
                {onSelectVoice && (
                    <button 
                        onClick={onSelectVoice} 
                        className="text-xs text-blue-400 hover:text-blue-300 underline"
                    >
                        {hasClonedVoice ? 'Change Voice' : 'Select Voice'}
                    </button>
                )}
            </div>

            {recordError && <p className="text-xs text-red-400">{recordError}</p>}
            <textarea
                value={dialogue.text}
                onChange={(e) => onDialogueChange({ text: e.target.value })}
                rows={2}
                placeholder={isTranscribing ? "Transcribing your dialogue..." : `Enter dialogue for ${character.name}...`}
                className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md resize-none text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={isProcessing}
            />
            {transcriptionError && <p className="text-xs text-red-400 mt-1">{transcriptionError}</p>}
            {estimatedSpeechSec > 0 && (() => {
                const windowSec = (parseFloat(dialogue.endTimecode) || 0) - (parseFloat(dialogue.startTimecode) || 0);
                const isOverflow = estimatedSpeechSec > windowSec + 1;
                return isOverflow ? (
                    <p className="text-xs text-amber-400 mt-1">
                        Dialogue needs ~{estimatedSpeechSec}s but time window is {windowSec.toFixed(0)}s. Text may be truncated. Extend end timecode or shorten dialogue.
                    </p>
                ) : null;
            })()}
            {sourceAudio && (
                 <div className="bg-gray-900/50 p-2 rounded-md space-y-2">
                     <div className="flex items-center gap-2">
                        <audio src={sourceAudio.url} controls className="w-full h-8" />
                        <button
                            onClick={handleResetRecording}
                            className="p-1.5 rounded-full hover:bg-red-500/20 text-red-400 flex-shrink-0"
                            aria-label="Delete recording and record again"
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    </div>
                 </div>
            )}
            <button
                onClick={onGenerateSpeech}
                disabled={!hasClonedVoice || !dialogue.text || isProcessing}
                className="w-full text-sm bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-1.5 px-3 rounded-md flex items-center justify-center"
            >
                {generationStatus === 'generating' ? <LoadingSpinner className="w-4 h-4 mr-2" /> : <MagicWandIcon className="w-4 h-4 mr-2" />}
                {generationStatus === 'generating' ? 'Generating...' : 'Generate Audio'}
            </button>
            {!hasClonedVoice && (
                <p className="text-xs text-yellow-400 mt-1">Please select a voice for this character to enable AI audio generation.</p>
            )}
            {generationStatus === 'error' && generationError && <p className="text-xs text-red-400">{generationError}</p>}
            {dialogue.clonedAudio && (
                 <div className="bg-green-900/30 p-2 rounded-md space-y-2 mt-2">
                    <p className="text-xs text-green-300 font-semibold">Generated Audio:</p>
                    <audio src={dialogue.clonedAudio.data} controls className="w-full h-8" />
                 </div>
            )}
        </div>
    );
};

export const VideoPreProductionModal = ({ shot, characters, onClose, onSave, projectData, onDeductCredits, onTrackCreditsUsed, onUpdateCharacter, onUpdateShotFields }) => {
    const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(true);
    const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [generatedVideoUrl, setGeneratedVideoUrl] = useState(null);
    const [activeTab, setActiveTab] = useState('prompt');
    const [showPromptEditor, setShowPromptEditor] = useState(false);
    const [referenceVideos, setReferenceVideos] = useState([]); // one generation only, not saved on shot
    const [audioReferences, setAudioReferences] = useState(shot.audioReferences || {});
    const handleReferenceChange = (category, ref) => {
        const next = { ...audioReferences };
        if (ref) next[category] = ref; else delete next[category];
        setAudioReferences(next);
        if (onUpdateShotFields) onUpdateShotFields(shot.id, { audioReferences: next });
    };
    
    const [showVoiceModal, setShowVoiceModal] = useState(false);
    const [selectingVoiceForCharId, setSelectingVoiceForCharId] = useState(null);
    
    const [timedActions, setTimedActions] = useState([]);

    // Feb 2026 — Auto-TTS tracking: which lineIds have we already tried
    // (successfully or not) so we don't loop on the effect. Reset when a
    // line's text or characterId changes.
    const autoTtsAttempted = useRef(new Map()); // lineId → "textHash|charId|voiceId"
    
    // Estimate speech duration for text (handles non-English scripts better)
    const estimateSpeechDuration = (text) => {
        if (!text || !text.trim()) return 0;
        const wordCount = text.trim().split(/\s+/).length;
        const charCount = text.trim().length;
        // Word-based: ~2.5 words/sec (English)
        // Character-based: ~0.08s per char for non-Latin scripts (Kannada, Hindi, etc.)
        const wordEstimate = Math.ceil(wordCount / 2.5);
        const charEstimate = Math.ceil(charCount * 0.08);
        // Use the LONGER estimate + 1s buffer for pauses
        return Math.max(2, Math.max(wordEstimate, charEstimate) + 1);
    };

    // `dialogues` is keyed by lineId (uuid). Each entry is ONE timecoded line:
    //   { characterId, text, startTimecode, endTimecode, clonedAudio?, order }
    // Previously this was keyed by characterId with all lines concatenated — that
    // collapsed narrator/character lines at different timecodes into a single
    // text block. Per-line keying lets the UI render each line as its own row
    // with editable timecodes (matching the script).
    const [dialogues, setDialogues] = useState(() => {
        const initial = {};
        const allRelevantChars = [...characters, ...projectData.characters.filter(c => c.category === 'narrator')];

        const parseT = (v) => {
            if (v === undefined || v === null) return null;
            if (typeof v === 'number') return v;
            const m = String(v).match(/[\d.]+/);
            return m ? parseFloat(m[0]) : null;
        };

        // Parse a single timecode like "00:03.5", "01:30", "3.5s", or "3.5"
        // into seconds. Supports MM:SS, MM:SS.s, and bare seconds.
        const parseTimeToSec = (str) => {
            if (str === null || str === undefined) return null;
            if (typeof str === 'number') return str;
            const trimmed = String(str).trim();
            if (!trimmed) return null;
            const colonMatch = trimmed.match(/^(\d+):(\d+(?:\.\d+)?)/);
            if (colonMatch) {
                return parseInt(colonMatch[1], 10) * 60 + parseFloat(colonMatch[2]);
            }
            const numMatch = trimmed.match(/^([\d.]+)\s*s?$/i);
            if (numMatch) return parseFloat(numMatch[1]);
            return null;
        };

        // Parse a panel timecode range like "00:03.5 - 00:07.5",
        // "00:00 – 00:02.5" (em-dash), or "3.5s - 7.5s" into { startSec, endSec }.
        const parsePanelTimecode = (timecode) => {
            if (!timecode || typeof timecode !== 'string') return null;
            const parts = timecode.split(/\s*[–-]\s*/);
            if (parts.length < 2) return null;
            const startSec = parseTimeToSec(parts[0]);
            const endSec = parseTimeToSec(parts[1]);
            if (startSec === null || endSec === null) return null;
            return { startSec, endSec };
        };

        // ---- Admin-authoritative timing source ----
        // Keep ALL admin pre-production entries per character (sorted by start
        // time) so we can pair the i-th user line with the i-th admin entry.
        const adminEntriesByChar = {};
        const preProdDialogues = Array.isArray(shot.preProduction?.dialogues) ? shot.preProduction.dialogues : [];
        for (const ad of preProdDialogues) {
            if (!ad?.characterId) continue;
            const start = parseT(ad.startTime ?? ad.start_time);
            const end = parseT(ad.endTime ?? ad.end_time);
            if (!adminEntriesByChar[ad.characterId]) adminEntriesByChar[ad.characterId] = [];
            adminEntriesByChar[ad.characterId].push({
                text: ad.text || '',
                startTime: start,
                endTime: end,
            });
        }
        Object.values(adminEntriesByChar).forEach(arr => {
            arr.sort((a, b) => (a.startTime ?? Infinity) - (b.startTime ?? Infinity));
        });

        // Sort shot.dialogues by their order/index/startTime if available
        const sortedDialogues = [...(shot.dialogues || [])].sort((a, b) => {
            if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
            if (a.index !== undefined && b.index !== undefined) return a.index - b.index;
            const aTime = parseFloat(a.startTime || a.start_time || '0');
            const bTime = parseFloat(b.startTime || b.start_time || '0');
            if (aTime !== bTime) return aTime - bTime;
            return 0;
        });

        console.log('🎭 Processing dialogues per-line:', sortedDialogues.length, 'user lines,',
            preProdDialogues.length, 'admin lines');

        // Per-character cursor for matching i-th user line ↔ i-th admin entry
        const perCharCursor = {};
        // Global sequential cursor for fallback when admin timing is missing
        let currentTime = 0;

        sortedDialogues.forEach((d, idx) => {
            const charId = d.characterId;
            if (!charId) return;
            const char = allRelevantChars.find(c => c.id === charId);
            if (!char) return;

            const perCharIdx = perCharCursor[charId] ?? 0;
            perCharCursor[charId] = perCharIdx + 1;

            const text = (d.text || '').trim();
            if (!text) return;
            const estimatedDuration = estimateSpeechDuration(text);

            // TIMING: prefer the i-th admin entry for this character, then fall back
            // to user-supplied startTime/endTime, then to a sequential cursor.
            const adminEntry = adminEntriesByChar[charId]?.[perCharIdx];
            const userStart = parseT(d.startTime ?? d.start_time);
            const userEnd = parseT(d.endTime ?? d.end_time);
            let start, end;
            if (adminEntry && adminEntry.startTime !== null && adminEntry.startTime !== undefined) {
                start = adminEntry.startTime;
                end = (adminEntry.endTime !== null && adminEntry.endTime !== undefined)
                    ? adminEntry.endTime
                    : start + estimatedDuration;
            } else if (userStart !== null) {
                start = userStart;
                end = userEnd !== null ? userEnd : start + estimatedDuration;
            } else {
                start = currentTime;
                end = currentTime + estimatedDuration;
                currentTime = end;
            }

            const lineId = uuidv4();
            initial[lineId] = {
                characterId: charId,
                text,
                startTimecode: `${start.toFixed(1)}s`,
                endTimecode: `${end.toFixed(1)}s`,
                order: d.order ?? idx,
            };
        });

        // Surface admin-only lines (admin has lines not authored by user)
        Object.entries(adminEntriesByChar).forEach(([charId, entries]) => {
            const usedCount = perCharCursor[charId] ?? 0;
            const remaining = entries.slice(usedCount);
            const char = allRelevantChars.find(c => c.id === charId);
            if (!char) return;
            remaining.forEach((entry, i) => {
                if (!entry.text) return;
                const start = entry.startTime ?? 0;
                const end = entry.endTime ?? (start + estimateSpeechDuration(entry.text));
                const lineId = uuidv4();
                initial[lineId] = {
                    characterId: charId,
                    text: entry.text,
                    startTimecode: `${start.toFixed(1)}s`,
                    endTimecode: `${end.toFixed(1)}s`,
                    order: 500 + usedCount + i,
                };
            });
        });

        // Panel-authoritative timing for BOTH narration and character dialogue.
        // When `shot.panels[]` has per-panel `voiceOver` strings with timecodes,
        // that IS the script's source of truth. Parse each panel's voiceOver:
        //   • "Narrator: text" / "VO: text" / unattributed plain text → narrator
        //   • "<CharacterName>: text" where CharacterName matches a project
        //     character → that character's dialogue
        // Override any lines built so far for these characters (panel data
        // wins). Decimals are preserved (3.5, 7.5, 11.5, 15.0).
        const narratorChar = projectData.characters.find(c => c.category === 'narrator');
        if (Array.isArray(shot?.panels)) {
            const nonNarratorChars = projectData.characters.filter(c => c.category !== 'narrator' && c.category !== 'element');

            // Match a speaker label to either the narrator or a project character.
            // Case-insensitive whole-word match, falls back to longest-prefix.
            const resolveSpeaker = (label) => {
                const norm = String(label || '').trim().toLowerCase();
                if (!norm) return null;
                if (/^(narrator|narration|vo|v\.o\.|voice ?over)$/i.test(label.trim())) {
                    return narratorChar || null;
                }
                // Exact match first
                for (const c of nonNarratorChars) {
                    const cName = (c.name || '').trim().toLowerCase();
                    if (cName && cName === norm) return c;
                }
                // First-token / contains fallback
                for (const c of nonNarratorChars) {
                    const cName = (c.name || '').trim().toLowerCase();
                    if (!cName) continue;
                    const firstToken = cName.split(/\s+/)[0];
                    if (firstToken && (norm === firstToken || norm.startsWith(firstToken + ' '))) return c;
                }
                return null;
            };

            // Parse a panel's voiceOver into an array of { speaker, text }.
            // Supports "Name: text" attribution per line. Multi-speaker panels
            // typically come on separate lines:
            //   "Maya: Hi.\nBob: Hello."
            // Plain unattributed text becomes a single narrator entry.
            const parseVoiceOver = (voiceOverStr) => {
                const str = String(voiceOverStr || '').trim();
                if (!str) return [];
                const lines = str.split(/\n+/).map(l => l.trim()).filter(Boolean);
                const segments = [];
                for (const line of lines) {
                    // "Name: text" — Name is up to 30 chars of letters/digits/space/punct
                    const m = line.match(/^([A-Za-z][A-Za-z0-9 ._'-]{0,29}?)\s*:\s+(.+)$/);
                    if (m) {
                        segments.push({ speakerLabel: m[1].trim(), text: m[2].trim() });
                    } else {
                        segments.push({ speakerLabel: 'Narrator', text: line });
                    }
                }
                return segments;
            };

            // Collect per-panel lines: each entry has a resolved character + timing
            const panelDerivedLines = [];
            shot.panels.forEach((panel, panelIdx) => {
                const voText = (panel?.voiceOver || '').trim();
                if (!voText) return;
                const tcRange = parsePanelTimecode(panel.timecode);
                if (!tcRange) return;
                const segments = parseVoiceOver(voText);
                if (segments.length === 0) return;
                // Distribute the panel's time window across multi-speaker segments
                // proportionally to text length (so longer lines get more time).
                const totalChars = segments.reduce((sum, s) => sum + s.text.length, 0) || 1;
                let cursor = tcRange.startSec;
                segments.forEach((seg, segIdx) => {
                    const speaker = resolveSpeaker(seg.speakerLabel);
                    // Fall back to narrator if we can't resolve the speaker
                    // (keeps the dialogue visible rather than dropping it).
                    const char = speaker || narratorChar;
                    if (!char) return;
                    const proportion = seg.text.length / totalChars;
                    const segStart = cursor;
                    const segEnd = segIdx === segments.length - 1
                        ? tcRange.endSec
                        : cursor + (tcRange.endSec - tcRange.startSec) * proportion;
                    cursor = segEnd;
                    panelDerivedLines.push({
                        characterId: char.id,
                        text: seg.text,
                        startSec: segStart,
                        endSec: segEnd,
                        order: panelIdx * 100 + segIdx,
                    });
                });
            });

            if (panelDerivedLines.length > 0) {
                // Remove existing lines for any character that has panel-derived
                // lines — panel data wins for that character.
                const overriddenCharIds = new Set(panelDerivedLines.map(l => l.characterId));
                Object.keys(initial).forEach(lineId => {
                    if (overriddenCharIds.has(initial[lineId].characterId)) {
                        delete initial[lineId];
                    }
                });
                panelDerivedLines.forEach(entry => {
                    const lineId = uuidv4();
                    initial[lineId] = {
                        characterId: entry.characterId,
                        text: entry.text,
                        startTimecode: `${entry.startSec.toFixed(1)}s`,
                        endTimecode: `${entry.endSec.toFixed(1)}s`,
                        order: entry.order,
                    };
                });
                console.log(`🎭 Sourced ${panelDerivedLines.length} dialogue/narration lines from shot.panels (panel timecodes are authoritative)`);
            }
        }

        // Narrator: parse `shot.narration` if no narrator line has been added yet.
        // Supports timecoded multi-line forms like:
        //   "[0-3.5s] Line one\n[3.5-7.5s] Line two"
        //   "@[0.0s-3.5s] Line one @[3.5s-7.5s] Line two"
        // Otherwise a single un-timed block becomes one line starting at 0s.
        if (narratorChar) {
            const hasNarratorLine = Object.values(initial).some(l => l.characterId === narratorChar.id);
            if (!hasNarratorLine && shot.narration) {
                const narrationStr = String(shot.narration);
                const timecodedRegex = /@?\[([0-9.]+)\s*s?\s*-\s*([0-9.]+)\s*s?\]\s*([^[]+?)(?=@?\[|$)/g;
                const matches = [...narrationStr.matchAll(timecodedRegex)];
                if (matches.length > 0) {
                    matches.forEach((m, i) => {
                        const start = parseFloat(m[1]);
                        const end = parseFloat(m[2]);
                        const text = (m[3] || '').trim();
                        if (!text) return;
                        const lineId = uuidv4();
                        initial[lineId] = {
                            characterId: narratorChar.id,
                            text,
                            startTimecode: `${start.toFixed(1)}s`,
                            endTimecode: `${end.toFixed(1)}s`,
                            order: i,
                        };
                    });
                } else {
                    const text = narrationStr.trim();
                    if (text) {
                        const dur = estimateSpeechDuration(text) || 3;
                        const lineId = uuidv4();
                        initial[lineId] = {
                            characterId: narratorChar.id,
                            text,
                            startTimecode: '0.0s',
                            endTimecode: `${dur.toFixed(1)}s`,
                            order: 0,
                        };
                    }
                }
            }
        }

        return initial;
    });

    // Group dialogues by characterId for tab rendering. Sorted by start time
    // within each character group so the UI shows the script's chronological
    // order even when multiple lines share a character.
    const groupDialoguesByCharacter = (dialoguesMap, charId) => {
        return Object.entries(dialoguesMap)
            .filter(([, line]) => line.characterId === charId)
            .sort(([, a], [, b]) => {
                const aStart = parseFloat(a.startTimecode) || 0;
                const bStart = parseFloat(b.startTimecode) || 0;
                if (aStart !== bStart) return aStart - bStart;
                return (a.order ?? 0) - (b.order ?? 0);
            })
            .map(([lineId, line]) => ({ lineId, ...line }));
    };

    const [speechGenStatus, setSpeechGenStatus] = useState({});

    const [textOverlays, setTextOverlays] = useState(shot.textOverlays || []);
    const narrator = projectData.characters.find(c => c.category === 'narrator');
    
    // Dynamic video models from backend
    const [availableVideoModels, setAvailableVideoModels] = useState(DEFAULT_VIDEO_MODELS);
    const [isLoadingModels, setIsLoadingModels] = useState(true);
    
    // Determine video model type based on available frames.
    //
    // Panel-based shots (`shot.panels[]` has at least one image) go through
    // their OWN model-picker branch in fetchModels(): only references-to-video
    // capable models are offered. So for panel shots we explicitly keep
    // hasBothFrames=false / hasOnlyOneFrame=false to prevent the start-end
    // frame or image-to-video branches from firing.
    const panelImageCount = Array.isArray(shot?.panels)
        ? shot.panels.filter(p => !!p?.image).length
        : 0;
    // Feb 2026 update #3 — Shot Pre-Production now treats refs-to-video as
    // the primary generation mode: a shot is "refs-to-video eligible" when
    // it has either (a) panel images, OR (b) at least one character/element
    // (via characterIds/elementIds) that carries an `image` URL. Only when
    // NEITHER is present do we fall back to the legacy start/end frame flow.
    const refEligibleCharacters = (() => {
        const all = Array.isArray(projectData?.characters) ? projectData.characters : [];
        const shotCharIds = Array.isArray(shot?.characterIds) ? shot.characterIds : [];
        const shotElemIds = Array.isArray(shot?.elementIds)   ? shot.elementIds   : [];
        return all.filter(c =>
            c && c.image && (shotCharIds.includes(c.id) || shotElemIds.includes(c.id))
        );
    })();
    const hasCastRefImages = refEligibleCharacters.length > 0;
    const isPanelBased    = panelImageCount > 0 || hasCastRefImages;
    const hasBothFrames   = !isPanelBased && !!(shot.startFrameImage && shot.endFrameImage);
    const hasOnlyOneFrame = !isPanelBased && !hasBothFrames && !!(shot.startFrameImage || shot.endFrameImage);
    
    // Fetch available video models from backend based on frame availability
    useEffect(() => {
        const fetchModels = async () => {
            setIsLoadingModels(true);
            try {
                let models = [];
                
                // ALWAYS use localStorage as the source of truth for model selections
                // The AIToolkitSelector saves selections to localStorage immediately when user clicks a model
                // projectData.modelPreferences may be stale/outdated
                let selectedModels = {};
                
                try {
                    const stored = localStorage.getItem('selectedAIModels');
                    if (stored) {
                        selectedModels = JSON.parse(stored);
                        console.log('🎬 Using selectedModels from localStorage (source of truth)');
                    }
                } catch (e) {
                    console.warn('🎬 Failed to parse selectedAIModels from localStorage:', e);
                }
                
                // Fallback to projectData only if localStorage is empty
                if (Object.keys(selectedModels).length === 0) {
                    if (projectData.modelPreferences?.selectedModels && Object.keys(projectData.modelPreferences.selectedModels).length > 0) {
                        selectedModels = projectData.modelPreferences.selectedModels;
                        console.log('🎬 Fallback: Using selectedModels from projectData.modelPreferences');
                    } else if (projectData.selectedModels && Object.keys(projectData.selectedModels).length > 0) {
                        selectedModels = projectData.selectedModels;
                        console.log('🎬 Fallback: Using selectedModels from projectData.selectedModels');
                    }
                }
                
                console.log('🎬 All selected models from AI Toolkit:', selectedModels);
                console.log('🎬 Available keys:', Object.keys(selectedModels));
                
                // Fast path: if pre-production stamped both model variants
                // onto this shot, pick the one matching the shot's CURRENT
                // frame state. Falls back to the legacy single-stamp and then
                // to the localStorage → list lookup below.
                //
                // Feb 13 2026 — For panel-based shots we intentionally SKIP
                // this fast path. The stamped `selectedVideoModelImageToVideo`
                // / `selectedVideoModelStartEndFrame` variants belong to a
                // different capability (image-to-video / start-end-frame)
                // and would silently override the user's AI Settings choice
                // for references-to-video. Instead we read directly from
                // localStorage below so the live tier selection wins.
                //
                // Feb 2026 update #3 — same treatment for cast-ref-only
                // shots (panels empty but characters/elements present).
                // Trust the outer `isPanelBased` so both panel and cast-ref
                // shots take the refs-to-video branch consistently.
                const isPanelBasedShotForStamp = isPanelBased;
                const ppForStamp = shot.preProduction || {};
                const stampedVariant = isPanelBasedShotForStamp
                    ? ppForStamp.selectedVideoModelReferencesToVideo
                    : (hasBothFrames
                        ? (ppForStamp.selectedVideoModelStartEndFrame || ppForStamp.selectedVideoModelImageToVideo)
                        : (ppForStamp.selectedVideoModelImageToVideo || ppForStamp.selectedVideoModelStartEndFrame));
                const stampedModelId = isPanelBasedShotForStamp
                    ? (stampedVariant?.id || null)
                    : ((stampedVariant?.id && stampedVariant.hasEndFrame === hasBothFrames)
                        ? stampedVariant.id
                        : (ppForStamp.selectedVideoModel?.id && ppForStamp.selectedVideoModel.hasEndFrame === hasBothFrames
                            ? ppForStamp.selectedVideoModel.id
                            : null));
                let defaultModelKey;
                if (stampedModelId) {
                    defaultModelKey = stampedModelId;
                    console.log('🎬 Using stamped selectedVideoModel from pre-production:', defaultModelKey, '(isPanelBased=', isPanelBasedShotForStamp, ', hasBothFrames=', hasBothFrames, ')');
                } else if (hasBothFrames) {
                    // For start-end-frame, look for the 'start-end-frame videos' capability
                    defaultModelKey = selectedModels['start-end-frame videos'] || 
                                      selectedModels['start-end-frame video'] ||
                                      selectedModels['startEndFrame'] ||
                                      selectedModels['start_end_frame_videos'];
                    console.log('🎬 Looking for start-end-frame default model:', defaultModelKey);
                } else {
                    // For image-to-video, look for the 'Image to video' capability
                    defaultModelKey = selectedModels['Image to video'] || 
                                      selectedModels['image-to-video'] ||
                                      selectedModels['imageToVideo'] ||
                                      selectedModels['image_to_video'];
                    console.log('🎬 Looking for image-to-video default model:', defaultModelKey);
                }
                
                // Panel-based shot? — show ONLY references-to-video capable models.
                // (Falls back to start-end-frame and image-to-video lists if the
                //  admin catalog does not yet expose a references-to-video
                //  capability — so the dropdown is never empty during rollout.)
                //
                // Feb 2026 update #3 — also enter the refs-to-video branch when
                // the shot has NO panels but has characters/elements in Cast &
                // World with images. The outer `isPanelBased` state already
                // encodes this, so we trust it here instead of recomputing a
                // panel-only variant (which was the previous bug — dropdown
                // showed image-to-video / start-end-frame models for cast-ref
                // only shots).
                const isPanelBasedShot = isPanelBased;

                if (isPanelBasedShot) {
                    console.log('🎬 Shot has PANEL structure - showing ONLY references-to-video models');
                    const allModels = await getAllVideoModels();
                    models = allModels.referencesToVideo || [];
                    if (models.length === 0) {
                        console.warn('⚠️ No references-to-video models in admin catalog yet — falling back to start-end-frame + image-to-video so dropdown is not empty');
                        models = [...(allModels.startEndFrame || []), ...(allModels.imageToVideo || [])];
                    }
                    // Feb 13 2026 — Case-insensitive lookup for the user's
                    // AI Settings pick. Admin catalogs sometimes save the
                    // capability key as `'References to video'` (capital R
                    // with spaces) while our checks were only trying
                    // lowercase variants — so the user's pick silently
                    // didn't apply. Now we scan every key in the localStorage
                    // map and pick any whose lowercased form matches a
                    // known refs-to-video alias.
                    // Also — the user's LIVE AI Settings pick always wins
                    // over any (possibly-stale) pre-production stamp for
                    // panel-based shots.
                    const REFS_ALIASES = new Set([
                        'references to video',
                        'references-to-video',
                        'references_to_video',
                        'referencestovideo',
                        'reference to video',
                        'reference-to-video',
                        'reference_to_video',
                        'referencetovideo',
                    ]);
                    let userPickedRefsKey = null;
                    for (const [k, v] of Object.entries(selectedModels || {})) {
                        if (REFS_ALIASES.has(String(k).toLowerCase().trim())) {
                            userPickedRefsKey = v;
                            break;
                        }
                    }
                    if (userPickedRefsKey) {
                        console.log('🎬 User picked refs-to-video in AI Settings:', userPickedRefsKey, '— overrides any pre-production stamp');
                        defaultModelKey = userPickedRefsKey;
                    } else if (!defaultModelKey) {
                        // No user pick and no stamp — leave defaultModelKey
                        // as-is and let the list pick its first entry.
                        console.warn('🎬 No refs-to-video selection found in AI Settings; dropdown will default to first model');
                    }
                    console.log('🎬 references-to-video default model key:', defaultModelKey);
                } else if (hasBothFrames) {
                    // Both frames available - show ONLY start-end-frame models
                    console.log('🎬 Shot has BOTH frames - showing ONLY start-end-frame video models');
                    const allModels = await getAllVideoModels();
                    models = allModels.startEndFrame || [];
                } else if (hasOnlyOneFrame) {
                    // Only one frame - show ONLY image-to-video models
                    console.log('🎬 Shot has ONE frame - showing ONLY image-to-video models');
                    const allModels = await getAllVideoModels();
                    models = allModels.imageToVideo || [];
                } else {
                    // No frames - show text-to-video or all available
                    console.log('🎬 Shot has NO frames - showing all video models');
                    const allModels = await getAllVideoModels();
                    models = [...(allModels.imageToVideo || []), ...(allModels.startEndFrame || [])];
                }
                
                console.log('🎬 Fetched video models:', models.map(m => m.name || m.key));
                console.log('🎬 Default model key from AI Toolkit:', defaultModelKey);
                
                if (models && models.length > 0) {
                    // Transform backend model format to component format
                    let transformedModels = models.map(m => ({
                        id: m.key || m.id || m.name,
                        name: m.name || m.key || m.id,
                        provider: m.provider || '',
                        description: m.description || '',
                        costEstimate: m.cost_estimate || '',
                        disabled: m.disabled || false,
                        // Keep the catalog resolution data (used by the resolution dropdown).
                        resolutions: m.resolutions || m.supported_resolutions || m.resolution_options || null,
                        default_resolution: m.default_resolution || null
                    }));
                    
                    // Remove duplicates while preserving order
                    transformedModels = transformedModels.filter((m, idx, arr) => 
                        arr.findIndex(x => x.id === m.id) === idx
                    );
                    
                    console.log('🎬 Transformed models before reorder:', transformedModels.map(m => ({ id: m.id, name: m.name })));
                    
                    // Put the default model from AI Toolkit at the TOP of the list
                    if (defaultModelKey) {
                        const defaultKeyLower = defaultModelKey.toLowerCase().trim();
                        console.log('🎬 Searching for default model with key:', defaultKeyLower);
                        
                        const defaultModelIndex = transformedModels.findIndex(m => {
                            const modelId = (m.id || '').toLowerCase().trim();
                            const modelName = (m.name || '').toLowerCase().trim();
                            
                            // Exact match first
                            const exactMatch = modelId === defaultKeyLower || modelName === defaultKeyLower;
                            
                            // Then partial/contains match
                            const partialMatch = modelId.includes(defaultKeyLower) || 
                                                 defaultKeyLower.includes(modelId) ||
                                                 modelName.includes(defaultKeyLower) ||
                                                 defaultKeyLower.includes(modelName);
                            
                            if (exactMatch || partialMatch) {
                                console.log(`🎬 Found match: model=${m.name}, id=${m.id}, exactMatch=${exactMatch}, partialMatch=${partialMatch}`);
                            }
                            
                            return exactMatch || partialMatch;
                        });
                        
                        console.log('🎬 Default model index in list:', defaultModelIndex);
                        
                        if (defaultModelIndex > 0) {
                            // Move default model to top
                            const [defaultModel] = transformedModels.splice(defaultModelIndex, 1);
                            transformedModels.unshift(defaultModel);
                            console.log('🎬 Moved default model to top:', defaultModel.name);
                        } else if (defaultModelIndex === 0) {
                            console.log('🎬 Default model already at top:', transformedModels[0].name);
                        } else {
                            console.warn('🎬 Default model not found in available models. Looking for:', defaultKeyLower);
                            console.warn('🎬 Available model IDs:', transformedModels.map(m => m.id));
                            console.warn('🎬 Available model names:', transformedModels.map(m => m.name));
                        }
                    } else {
                        console.warn('🎬 No default model key found in AI Toolkit selections');
                    }
                    
                    console.log('🎬 Final video models (default first):', transformedModels.map(m => m.name));
                    setAvailableVideoModels(transformedModels);
                } else {
                    console.warn('No video models from backend, using defaults');
                    setAvailableVideoModels(DEFAULT_VIDEO_MODELS);
                }
            } catch (err) {
                console.warn('Failed to fetch video models, using defaults:', err);
                setAvailableVideoModels(DEFAULT_VIDEO_MODELS);
            } finally {
                setIsLoadingModels(false);
            }
        };
        fetchModels();
    }, [hasBothFrames, hasOnlyOneFrame, isPanelBased, projectData.modelPreferences, projectData.selectedModels]);
    
    const getInitialVideoModel = () => {
        // The default model from AI Toolkit is already at position 0 (moved to top during fetch)
        // So we just select the first non-disabled model
        const firstAvailableModel = availableVideoModels.find(m => !m.disabled);
        if (firstAvailableModel) {
            console.log('🎬 Using default model:', firstAvailableModel.name);
            return firstAvailableModel.id;
        }
        return availableVideoModels[0]?.id || 'wan-2.2';
    };

    const [videoModel, setVideoModel] = useState(getInitialVideoModel());
    const [cameraShot, setCameraShot] = useState(CAMERA_SHOTS[0]);
    const [sfx, setSfx] = useState('');
    const [vfx, setVfx] = useState('');
    const [bgm, setBgm] = useState('');
    const [ambient, setAmbient] = useState('');
    const [resolution, setResolution] = useState('auto');
    // Feb 2026 — resolution options are now derived from the selected
    // model so the dropdown always shows what THAT model supports.
    const resolutionOptions = useMemo(() => {
        const modelObj = availableVideoModels.find(m => m.id === videoModel) || videoModel;
        return getResolutionOptionsForModel(modelObj);
    }, [videoModel, availableVideoModels]);
    const resolutionCredits = useMemo(() => getResolutionCreditsForModel(availableVideoModels.find(m => m.id === videoModel)), [videoModel, availableVideoModels]);
    
    // Compute content-driven duration: based on dialogue and narration needs
    const computeContentDuration = () => {
        // 1. Max dialogue end time (speech needs real time to play)
        const maxDialogueEnd = Object.values(dialogues).reduce((max, d) => {
            const end = parseFloat(d.endTimecode) || 0;
            return Math.max(max, end);
        }, 0);
        
        // 2. Narration duration (speech needs real time to play)
        const narrationText = shot.narration || '';
        const narrationDuration = estimateSpeechDuration(narrationText);
        
        // Minimum 8 seconds (most video models support at least 8s)
        // Duration stretches only if dialogues/narration require more time
        return Math.max(8, Math.ceil(maxDialogueEnd), narrationDuration);
    };
    
    const [duration, setDuration] = useState(computeContentDuration);
    
    // Update video model when available models are loaded
    // Always select the first model (which is the default from AI Toolkit, moved to top)
    useEffect(() => {
        if (!isLoadingModels && availableVideoModels.length > 0) {
            // The first model in the list is the default from AI Toolkit
            const defaultModel = availableVideoModels.find(m => !m.disabled) || availableVideoModels[0];
            if (defaultModel && defaultModel.id !== videoModel) {
                console.log('🎬 Setting video model to default:', defaultModel.name);
                setVideoModel(defaultModel.id);
            }
        }
    }, [availableVideoModels, isLoadingModels]);

    const resolutionModelKeyRef = useRef(null);
    useEffect(() => {
        // Feb 2026 — whenever the selected model changes, snap the
        // resolution to the model's default (or keep the current one if
        // the model still supports it). Keeps the dropdown and payload
        // in sync without stale picks sneaking into the request body.
        // A new model (or its catalog data arriving) resets to the catalog default;
        // otherwise the user's pick is kept.
        const modelObj = availableVideoModels.find(m => m.id === videoModel) || videoModel;
        const options = getResolutionOptionsForModel(modelObj);
        const def = getDefaultResolutionForModel(modelObj);
        const key = `${videoModel}|${def}|${options.join(',')}`;
        const changed = resolutionModelKeyRef.current !== key;
        resolutionModelKeyRef.current = key;
        setResolution(prev => (!changed && options.includes(prev) ? prev : def));
    }, [videoModel, availableVideoModels]);

    const durationProfile = useMemo(() => {
        const modelObj = availableVideoModels.find(m => m.id === videoModel) || videoModel;
        return getVideoDurationProfile(modelObj);
    }, [videoModel, availableVideoModels]);

    useEffect(() => {
        // Snap duration to the model's render profile (e.g. Seedance B2.5 → 15s default, 30s max).
        if (!durationProfile) return;
        const modelObj = availableVideoModels.find(m => m.id === videoModel) || videoModel;
        setDuration(applyModelDurationProfile(computeContentDuration(), modelObj));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [durationProfile, videoModel, availableVideoModels]);

    // State for detailed production prompt from backend
    const [productionPrompt, setProductionPrompt] = useState(null);

    useEffect(() => {
        const fetchPrompt = async () => {
            setError(null);
            setIsGeneratingPrompt(true);
            try {
                // PRE-FILL: If shot has pre-production data from the two-pass flow, use it directly
                if (shot.preProduction && Object.keys(shot.preProduction).length > 0) {
                    console.log('✨ Pre-filling VideoPreProductionModal from shot.preProduction');
                    const preProd = shot.preProduction;

                    // Normalize into the same shape as the backend production prompt
                    const normalizedPrompt = {
                        video_prompt: {
                            main_prompt: preProd.cinematicPrompt || preProd.cinematic_prompt || preProd.main_prompt || preProd.prompt || shot.description || ''
                        },
                        character_actions: preProd.character_actions || preProd.actions || [],
                        scene_actions: preProd.scene_actions || preProd.sceneActions || preProd.environment_actions || [],
                        camera: preProd.camera || {},
                        audio: preProd.audio || {},
                        vfx: preProd.vfx || preProd.visual_effects || []
                    };
                    setProductionPrompt(normalizedPrompt);

                    // Pre-fill timed actions
                    const rawActions = normalizedPrompt.character_actions;
                    let flattenedActions = [];
                    if (Array.isArray(rawActions) && rawActions.length > 0) {
                        if (rawActions[0]?.timeline || rawActions[0]?.character_name) {
                            rawActions.forEach((charAction) => {
                                const charName = charAction.character_name || 'Character';
                                (charAction.timeline || []).forEach((entry) => {
                                    let startSec = 0;
                                    const tc = entry.timecode || entry.time || '0';
                                    if (String(tc).includes(':')) {
                                        const [mm, ss] = String(tc).split(':');
                                        startSec = (parseInt(mm) || 0) * 60 + (parseFloat(ss) || 0);
                                    } else {
                                        startSec = parseFloat(tc) || 0;
                                    }
                                    const durMs = entry.duration_ms || 2000;
                                    const endSec = startSec + durMs / 1000;
                                    const text = entry.action || entry.description || '';
                                    flattenedActions.push({
                                        id: uuidv4(),
                                        startTime: `${startSec.toFixed(1)}s`,
                                        endTime: `${endSec.toFixed(1)}s`,
                                        action: `${charName}: ${text}${entry.emotion ? ` [${entry.emotion}]` : ''}`
                                    });
                                });
                            });
                        } else {
                            flattenedActions = rawActions.map((a, idx) => {
                                const parseT = (v) => {
                                    if (v === undefined || v === null) return null;
                                    if (typeof v === 'number') return `${v.toFixed(1)}s`;
                                    return String(v).endsWith('s') ? v : `${parseFloat(v).toFixed(1)}s`;
                                };
                                return {
                                    id: uuidv4(),
                                    startTime: parseT(a.start_time ?? a.startTime ?? a.start) || `${(idx * (duration / rawActions.length)).toFixed(1)}s`,
                                    endTime: parseT(a.end_time ?? a.endTime ?? a.end) || `${((idx + 1) * (duration / rawActions.length)).toFixed(1)}s`,
                                    action: a.action || a.description || a.text || ''
                                };
                            });
                        }
                    }

                    if (flattenedActions.length === 0 && normalizedPrompt.video_prompt.main_prompt) {
                        flattenedActions = [{
                            id: uuidv4(),
                            startTime: '0.0s',
                            endTime: `${duration.toFixed(1)}s`,
                            action: normalizedPrompt.video_prompt.main_prompt
                        }];
                    }
                    // Merge non-character beats (product / landscape / animal / VFX)
                    // emitted by the admin LLM under `scene_actions[]`. These have
                    // their own timecodes and live alongside `character_actions[]`.
                    const sceneRows = flattenSceneActions(normalizedPrompt.scene_actions);
                    // Same diagnostic as the regenerate path so we can tell
                    // whether the cached preProduction blob was stamped BEFORE
                    // scene_actions support landed. If `scene_actions` is
                    // missing from the cache, the user must re-run pre-production.
                    console.log(`🎬 [fast-path] scene_actions diagnostic — preProduction keys: ${Object.keys(preProd).join(', ')}`);
                    console.log(`   ▸ scene_actions count in cache: ${Array.isArray(normalizedPrompt.scene_actions) ? normalizedPrompt.scene_actions.length : 'missing'}`);
                    console.log(`   ▸ flattened rows: ${sceneRows.length}`);
                    if (sceneRows.length === 0 && !Array.isArray(normalizedPrompt.scene_actions)) {
                        console.warn('🎬 [fast-path] No scene_actions in cached preProduction — this shot was pre-produced BEFORE scene_actions support shipped. Re-run pre-production for this shot to populate them.');
                    }
                    if (sceneRows.length > 0) {
                        flattenedActions = [...flattenedActions, ...sceneRows]
                            .sort((a, b) => (parseFloat(a.startTime) || 0) - (parseFloat(b.startTime) || 0));
                    }

                    // Feb 2026 — Always merge panel-derived rows (camera + staging).
                    // Even when the admin backend emitted scene_actions, these ensure
                    // the user's own panel breakdown (Camera Movement + Visual
                    // Composition) surfaces on the timeline.
                    const panelRows = extractSceneActionsFromPanels(parsePanelsFromDescription(shot?.description || ''));
                    if (panelRows.length > 0) {
                        console.log(`🎬 [fast-path] Adding ${panelRows.length} panel-derived rows (camera + staging)`);
                        flattenedActions = [...flattenedActions, ...panelRows]
                            .sort((a, b) => (parseFloat(a.startTime) || 0) - (parseFloat(b.startTime) || 0));
                    }

                    if (flattenedActions.length > 0) setTimedActions(flattenedActions);

                    // Pre-fill camera
                    if (normalizedPrompt.camera?.movement) {
                        const match = CAMERA_SHOTS.find(c =>
                            c.toLowerCase().includes(normalizedPrompt.camera.movement.toLowerCase()) ||
                            normalizedPrompt.camera.movement.toLowerCase().includes(c.toLowerCase())
                        );
                        if (match) setCameraShot(match);
                    }

                    // Pre-fill audio (BGM, SFX, Ambient)
                    if (normalizedPrompt.audio) {
                        const a = normalizedPrompt.audio;
                        if (a.bgm) {
                            const text = a.bgm.description || a.bgm.text || '';
                            const mood = a.bgm.mood ? ` (${a.bgm.mood})` : '';
                            const tempo = a.bgm.tempo_bpm ? ` - ${a.bgm.tempo_bpm} BPM` : '';
                            if (text) setBgm(`${text}${mood}${tempo}`.trim());
                        }
                        if (Array.isArray(a.sfx) && a.sfx.length > 0) {
                            setSfx(a.sfx.map(s => {
                                const snd = s.sound || s.description || '';
                                const t = s.timecode ? ` @${s.timecode}` : '';
                                return `${snd}${t}`;
                            }).filter(Boolean).join('; '));
                        }
                        if (a.ambient?.description) setAmbient(a.ambient.description);
                    }

                    // Pre-fill VFX
                    const vfxData = normalizedPrompt.vfx;
                    if (Array.isArray(vfxData) && vfxData.length > 0) {
                        setVfx(vfxData.map(v => v.effect || v.description || v).filter(Boolean).join('; '));
                    } else if (typeof vfxData === 'string' && vfxData) {
                        setVfx(vfxData);
                    }

                    console.log('✅ Pre-fill complete — skipping live prompt API call');
                    return;
                }

                // Try to get detailed production prompt from backend first
                try {
                    console.log('🎬 Fetching detailed video production prompt from backend...');
                    
                    // Get scene context
                    const scene = projectData.scenes?.find(s => s.shots?.some(sh => sh.id === shot.id));
                    const sceneContext = scene?.description || scene?.title || '';
                    
                    // Get main character name
                    const mainCharacter = characters[0];
                    const characterName = mainCharacter?.name || '';
                    
                    const detailedPrompt = await generateVideoProductionPrompt({
                        shotDescription: shot.description || '',
                        characterName: characterName,
                        sceneContext: sceneContext,
                        durationSeconds: duration,
                        aspectRatio: projectData.aspectRatio || '16:9',
                        mood: shot.mood || scene?.mood || '',
                        genre: projectData.genre || '',
                        startFrameImage: shot.startFrameImage || null,
                        endFrameImage: shot.endFrameImage || null
                    });
                    
                    if (detailedPrompt) {
                        console.log('✅ Detailed production prompt received from backend');
                        console.log('📋 Full detailedPrompt structure:', JSON.stringify(detailedPrompt, null, 2));
                        setProductionPrompt(detailedPrompt);
                        
                        // Extract the main video prompt text
                        const mainPromptText = detailedPrompt.video_prompt?.main_prompt || 
                                               detailedPrompt.video_prompt?.prompt ||
                                               detailedPrompt.main_prompt ||
                                               detailedPrompt.prompt || '';
                        
                        console.log('📝 Main prompt text:', mainPromptText?.substring(0, 200));
                        
                        // Use character_actions from backend if available
                        const rawActions = detailedPrompt.character_actions || 
                                       detailedPrompt.actions || 
                                       detailedPrompt.video_prompt?.actions ||
                                       [];
                        
                        console.log('🎬 Raw actions from backend:', JSON.stringify(rawActions, null, 2));
                        
                        // Flatten the nested character_actions structure
                        // Backend returns: [{character_name: "Anika", timeline: [{timecode, action, duration_ms}]}, ...]
                        // We need: [{startTime, endTime, action}, ...]
                        let flattenedActions = [];
                        
                        if (rawActions && rawActions.length > 0) {
                            // Check if it's the nested format (with character_name and timeline)
                            if (rawActions[0]?.timeline || rawActions[0]?.character_name) {
                                console.log('📋 Detected nested character_actions format, flattening...');
                                console.log('📋 Number of character blocks:', rawActions.length);
                                
                                rawActions.forEach((charAction, charIndex) => {
                                    const charName = charAction.character_name || 'Character';
                                    const timeline = charAction.timeline || [];
                                    
                                    console.log(`📋 Processing character ${charIndex + 1}: ${charName}, timeline entries: ${timeline.length}`);
                                    
                                    timeline.forEach((timelineItem, timelineIndex) => {
                                        // Parse timecode (format: "00:00" or "00:2.5" or "00:04")
                                        let startSeconds = 0;
                                        const timecode = timelineItem.timecode || '00:00';
                                        
                                        // Handle different timecode formats
                                        if (timecode.includes(':')) {
                                            const parts = timecode.split(':');
                                            const minutes = parseInt(parts[0]) || 0;
                                            const seconds = parseFloat(parts[1]) || 0;
                                            startSeconds = minutes * 60 + seconds;
                                        } else {
                                            startSeconds = parseFloat(timecode) || 0;
                                        }
                                        
                                        // Calculate end time from duration_ms or estimate
                                        const durationMs = timelineItem.duration_ms || 2000;
                                        const endSeconds = startSeconds + (durationMs / 1000);
                                        
                                        // Build action text with character name and emotion
                                        const actionText = timelineItem.action || timelineItem.description || '';
                                        const emotion = timelineItem.emotion ? ` [${timelineItem.emotion}]` : '';
                                        const fullActionText = `${charName}: ${actionText}${emotion}`;
                                        
                                        console.log(`📋 Action ${timelineIndex + 1} for ${charName}: "${actionText}" (${timecode} -> ${startSeconds.toFixed(1)}s-${endSeconds.toFixed(1)}s)`);
                                        
                                        flattenedActions.push({
                                            id: uuidv4(),
                                            startTime: `${startSeconds.toFixed(1)}s`,
                                            endTime: `${endSeconds.toFixed(1)}s`,
                                            action: fullActionText,
                                            character: charName,
                                            emotion: timelineItem.emotion,
                                            rawAction: actionText
                                        });
                                    });
                                });
                                
                                // Sort by start time
                                flattenedActions.sort((a, b) => {
                                    const aStart = parseFloat(a.startTime);
                                    const bStart = parseFloat(b.startTime);
                                    return aStart - bStart;
                                });
                                
                                console.log('📋 Final flattened actions count:', flattenedActions.length);
                                console.log('📋 Final actions:', flattenedActions.map(a => ({
                                    time: `${a.startTime}-${a.endTime}`,
                                    action: a.action
                                })));
                            } else {
                                // It's already a flat format
                                console.log('📋 Detected flat actions format');
                                flattenedActions = rawActions.map((action, idx) => {
                                    const actionText = action.action || action.description || action.text || action.prompt || '';
                                    const startTime = action.start_time || action.startTime || action.start || `${(idx * (duration / rawActions.length)).toFixed(1)}s`;
                                    const endTime = action.end_time || action.endTime || action.end || `${((idx + 1) * (duration / rawActions.length)).toFixed(1)}s`;
                                    
                                    return {
                                        id: uuidv4(),
                                        startTime: typeof startTime === 'number' ? `${startTime.toFixed(1)}s` : startTime,
                                        endTime: typeof endTime === 'number' ? `${endTime.toFixed(1)}s` : endTime,
                                        action: actionText
                                    };
                                });
                            }
                        }
                        
                        // Merge non-character beats from the admin's
                        // `scene_actions[]` field (drone shots, product
                        // hero shots, animal close-ups, environmental
                        // establishers, VFX). They share the same timeline
                        // as character actions but have no `character_name`.
                        // Pulled OUT of the `flattenedActions.length > 0` gate
                        // so a shot with ONLY scene actions (no characters)
                        // still gets rows.
                        {
                            const rawSceneActions = detailedPrompt.scene_actions
                                || detailedPrompt.video_prompt?.scene_actions
                                || detailedPrompt.environment_actions
                                || [];
                            const sceneRows = flattenSceneActions(rawSceneActions);
                            // Diagnostic: surface what the proxy returned so
                            // we can pinpoint where `scene_actions` is being
                            // dropped (admin LLM vs user backend proxy vs FE).
                            console.log(`🎬 scene_actions diagnostic — admin response keys: ${Object.keys(detailedPrompt).join(', ')}`);
                            console.log(`   ▸ scene_actions count: ${Array.isArray(rawSceneActions) ? rawSceneActions.length : 'not an array'}`);
                            console.log(`   ▸ flattened rows: ${sceneRows.length}`);
                            if (sceneRows.length > 0) {
                                console.log(`🎬 Merging ${sceneRows.length} scene_actions row(s) into the timeline`);
                                flattenedActions = [...flattenedActions, ...sceneRows];
                            } else if (Array.isArray(rawSceneActions) && rawSceneActions.length > 0) {
                                console.warn('🎬 scene_actions array received but every entry was invalid (no action text or unparseable timing). First entry:', rawSceneActions[0]);
                            } else {
                                console.warn('🎬 scene_actions is missing or empty in the admin response — either admin LLM did not emit it OR user-backend proxy stripped it. Check the Network tab raw response for /ai/generate-video-production-prompt.');
                            }
                        }

                        // Feb 2026 — Always merge panel-derived rows so the
                        // user's own Camera Movement + Visual Composition
                        // beats surface on the timeline regardless of what
                        // the admin LLM returned.
                        {
                            const panelRows = extractSceneActionsFromPanels(parsePanelsFromDescription(shot?.description || ''));
                            if (panelRows.length > 0) {
                                console.log(`🎬 [regen] Adding ${panelRows.length} panel-derived rows (camera + staging)`);
                                flattenedActions = [...flattenedActions, ...panelRows];
                            }
                        }

                        if (flattenedActions.length > 0) {
                            // Sort the merged set by start time
                            flattenedActions.sort((a, b) =>
                                (parseFloat(a.startTime) || 0) - (parseFloat(b.startTime) || 0)
                            );

                            // Scale actions to span the full video duration if backend returned shorter timecodes
                            const maxActionEnd = flattenedActions.reduce((max, a) => {
                                return Math.max(max, parseFloat(a.endTime) || 0);
                            }, 0);
                            
                            if (maxActionEnd > 0 && maxActionEnd < duration - 1) {
                                const scale = duration / maxActionEnd;
                                console.log(`⏱️ Scaling action timecodes from ${maxActionEnd.toFixed(1)}s to ${duration}s (scale: ${scale.toFixed(2)}x)`);
                                flattenedActions = flattenedActions.map(a => ({
                                    ...a,
                                    startTime: `${(parseFloat(a.startTime) * scale).toFixed(1)}s`,
                                    endTime: `${(parseFloat(a.endTime) * scale).toFixed(1)}s`
                                }));
                            }
                            
                            console.log(`✅ Setting ${flattenedActions.length} timed actions`);
                            setTimedActions(flattenedActions);
                            
                            // Update camera shot if provided
                            if (detailedPrompt.camera?.movement) {
                                const matchingCamera = CAMERA_SHOTS.find(c => 
                                    c.toLowerCase().includes(detailedPrompt.camera.movement.toLowerCase()) ||
                                    detailedPrompt.camera.movement.toLowerCase().includes(c.toLowerCase())
                                );
                                if (matchingCamera) {
                                    setCameraShot(matchingCamera);
                                }
                            }
                            
                            // Extract audio data (BGM, SFX, Ambient) from backend response
                            if (detailedPrompt.audio) {
                                console.log('🎵 Audio data from backend:', detailedPrompt.audio);
                                
                                // Extract BGM
                                if (detailedPrompt.audio.bgm) {
                                    const bgmData = detailedPrompt.audio.bgm;
                                    const bgmText = bgmData.description || '';
                                    const bgmMood = bgmData.mood ? ` (${bgmData.mood})` : '';
                                    const bgmTempo = bgmData.tempo_bpm ? ` - ${bgmData.tempo_bpm} BPM` : '';
                                    setBgm(`${bgmText}${bgmMood}${bgmTempo}`.trim());
                                    console.log('🎵 BGM set:', bgmText);
                                }
                                
                                // Extract SFX
                                if (detailedPrompt.audio.sfx && Array.isArray(detailedPrompt.audio.sfx)) {
                                    const sfxDescriptions = detailedPrompt.audio.sfx
                                        .map(s => {
                                            const sound = s.sound || s.description || '';
                                            const time = s.timecode ? ` @${s.timecode}` : '';
                                            return `${sound}${time}`;
                                        })
                                        .filter(s => s.trim())
                                        .join('; ');
                                    setSfx(sfxDescriptions);
                                    console.log('🔊 SFX set:', sfxDescriptions);
                                }
                                
                                // Extract Ambient sounds
                                if (detailedPrompt.audio.ambient) {
                                    const ambientData = detailedPrompt.audio.ambient;
                                    const ambientText = ambientData.description || '';
                                    setAmbient(ambientText);
                                    console.log('🌿 Ambient set:', ambientText);
                                }
                            }
                            
                            // Extract VFX if present
                            if (detailedPrompt.vfx || detailedPrompt.visual_effects) {
                                const vfxData = detailedPrompt.vfx || detailedPrompt.visual_effects;
                                if (Array.isArray(vfxData)) {
                                    const vfxDescriptions = vfxData
                                        .map(v => v.effect || v.description || v)
                                        .filter(v => v)
                                        .join('; ');
                                    setVfx(vfxDescriptions);
                                    console.log('✨ VFX set:', vfxDescriptions);
                                } else if (typeof vfxData === 'string') {
                                    setVfx(vfxData);
                                }
                            }
                            
                            return; // Successfully used backend prompt
                        } else if (mainPromptText) {
                            // Fallback: Use main_prompt as a single action if no character_actions
                            console.log('📋 No character_actions found, using main_prompt as single action');
                            setTimedActions([{
                                id: uuidv4(),
                                startTime: '0.0s',
                                endTime: `${duration.toFixed(1)}s`,
                                action: mainPromptText
                            }]);
                            
                            // Update camera shot if provided
                            if (detailedPrompt.camera?.movement) {
                                const matchingCamera = CAMERA_SHOTS.find(c => 
                                    c.toLowerCase().includes(detailedPrompt.camera.movement.toLowerCase()) ||
                                    detailedPrompt.camera.movement.toLowerCase().includes(c.toLowerCase())
                                );
                                if (matchingCamera) {
                                    setCameraShot(matchingCamera);
                                }
                            }
                            
                            // Extract audio data (BGM, SFX, Ambient) from backend response
                            if (detailedPrompt.audio) {
                                console.log('🎵 Audio data from backend:', detailedPrompt.audio);
                                
                                // Extract BGM
                                if (detailedPrompt.audio.bgm) {
                                    const bgmData = detailedPrompt.audio.bgm;
                                    const bgmText = bgmData.description || '';
                                    const bgmMood = bgmData.mood ? ` (${bgmData.mood})` : '';
                                    const bgmTempo = bgmData.tempo_bpm ? ` - ${bgmData.tempo_bpm} BPM` : '';
                                    setBgm(`${bgmText}${bgmMood}${bgmTempo}`.trim());
                                    console.log('🎵 BGM set:', bgmText);
                                }
                                
                                // Extract SFX
                                if (detailedPrompt.audio.sfx && Array.isArray(detailedPrompt.audio.sfx)) {
                                    const sfxDescriptions = detailedPrompt.audio.sfx
                                        .map(s => {
                                            const sound = s.sound || s.description || '';
                                            const time = s.timecode ? ` @${s.timecode}` : '';
                                            return `${sound}${time}`;
                                        })
                                        .filter(s => s.trim())
                                        .join('; ');
                                    setSfx(sfxDescriptions);
                                    console.log('🔊 SFX set:', sfxDescriptions);
                                }
                                
                                // Extract Ambient sounds
                                if (detailedPrompt.audio.ambient) {
                                    const ambientData = detailedPrompt.audio.ambient;
                                    const ambientText = ambientData.description || '';
                                    setAmbient(ambientText);
                                    console.log('🌿 Ambient set:', ambientText);
                                }
                            }
                            
                            // Extract VFX if present
                            if (detailedPrompt.vfx || detailedPrompt.visual_effects) {
                                const vfxData = detailedPrompt.vfx || detailedPrompt.visual_effects;
                                if (Array.isArray(vfxData)) {
                                    const vfxDescriptions = vfxData
                                        .map(v => v.effect || v.description || v)
                                        .filter(v => v)
                                        .join('; ');
                                    setVfx(vfxDescriptions);
                                    console.log('✨ VFX set:', vfxDescriptions);
                                } else if (typeof vfxData === 'string') {
                                    setVfx(vfxData);
                                }
                            }
                            
                            return; // Successfully used backend prompt
                        }
                    }
                } catch (backendErr) {
                    console.warn('⚠️ Backend production prompt failed, falling back to local generation:', backendErr.message);
                }
                
                // Fallback to local generation if backend fails
                console.log('🎬 Using local video prompt generation...');
                const generatedActions = await generateVideoPrompt(shot, characters, projectData.script, duration);
                let localActions = generatedActions.map(a => ({ ...a, id: uuidv4() }));
                
                // Scale local actions to span the full duration if needed
                const maxLocalEnd = localActions.reduce((max, a) => Math.max(max, parseFloat(a.endTime) || 0), 0);
                if (maxLocalEnd > 0 && maxLocalEnd < duration - 1) {
                    const scale = duration / maxLocalEnd;
                    localActions = localActions.map(a => ({
                        ...a,
                        startTime: `${(parseFloat(a.startTime) * scale).toFixed(1)}s`,
                        endTime: `${(parseFloat(a.endTime) * scale).toFixed(1)}s`
                    }));
                }
                
                setTimedActions(localActions);
            } catch (err) {
                console.error(err);
                setError("Failed to generate a timed prompt. Please write one manually.");
                setTimedActions([{ id: uuidv4(), startTime: "0.0s", endTime: `${duration.toFixed(1)}s`, action: shot.description || '' }]);
            } finally {
                setIsGeneratingPrompt(false);
            }
        };

        fetchPrompt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shot.id]); // Only re-fetch when shot changes, not when duration changes
    
    const handleAddTimedAction = () => {
        const lastTime = timedActions.length > 0 ? timedActions[timedActions.length - 1].endTime : "0.0s";
        const newAction = { id: uuidv4(), startTime: lastTime, endTime: lastTime, action: "" };
        setTimedActions(prev => [...prev, newAction]);
    };

    const handleRemoveTimedAction = (id) => {
        setTimedActions(prev => prev.filter(a => a.id !== id));
    };
    
    const handleUpdateTimedAction = (id, updates) => {
        setTimedActions(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a));
    };

    const handleDialogueChange = (lineId, update) => {
        setDialogues(prev => {
            const current = prev[lineId];
            if (!current) return prev;
            const merged = { ...current, ...update };

            // Auto-extend end timecode when text changes and would exceed the window
            if (update.text !== undefined) {
                const needed = estimateSpeechDuration(merged.text);
                const startSec = parseFloat(merged.startTimecode) || 0;
                const currentEnd = parseFloat(merged.endTimecode) || 0;
                const currentWindow = currentEnd - startSec;
                if (needed > currentWindow) {
                    merged.endTimecode = `${(startSec + needed).toFixed(1)}s`;
                }
            }

            return { ...prev, [lineId]: merged };
        });
    };

    const handleAddDialogueLine = (characterId) => {
        // New line starts immediately after the last line for this character.
        const existing = Object.values(dialogues).filter(l => l.characterId === characterId);
        const lastEnd = existing.reduce((max, l) => {
            const e = parseFloat(l.endTimecode) || 0;
            return Math.max(max, e);
        }, 0);
        const start = lastEnd;
        const end = lastEnd + 3;
        const lineId = uuidv4();
        setDialogues(prev => ({
            ...prev,
            [lineId]: {
                characterId,
                text: '',
                startTimecode: `${start.toFixed(1)}s`,
                endTimecode: `${end.toFixed(1)}s`,
                order: existing.length,
            }
        }));
    };

    const handleRemoveDialogueLine = (lineId) => {
        setDialogues(prev => {
            const next = { ...prev };
            delete next[lineId];
            return next;
        });
    };
    
    const handleVoiceSelect = (voiceId, voiceName) => {
        if (!selectingVoiceForCharId || !onUpdateCharacter) return;
        
        const character = projectData.characters.find(c => c.id === selectingVoiceForCharId);
        if (character) {
            onUpdateCharacter({
                ...character,
                elevenLabsVoiceId: voiceId,
                audio: { ...character.audio, name: voiceName }
            });
        }
        setShowVoiceModal(false);
        setSelectingVoiceForCharId(null);
    };

    const handleTriggerGenerateSpeech = async (lineId, text) => {
        const line = dialogues[lineId];
        if (!line) return;
        const character = projectData.characters.find(c => c.id === line.characterId);
        const voiceId = character?.elevenLabsVoiceId || character?.voiceId;
        if (!voiceId || !text) return;

        setSpeechGenStatus(prev => ({ ...prev, [lineId]: { status: 'generating', error: null } }));

        try {
            // Feb 2026 — Call generateSpeech directly so we can capture BOTH
            // the base64 data URI (for immediate <audio> preview) AND the
            // GCS `audio_url` (used later in the video generation payload).
            const speech = await generateSpeech(text, voiceId);
            let dataUri = null;
            if (speech.audioData) {
                const b64 = String(speech.audioData).trim();
                dataUri = b64.startsWith('data:') ? b64 : `data:audio/mpeg;base64,${b64}`;
            } else if (speech.audioUrl) {
                // Only URL came back — fetch and inline for preview.
                try {
                    dataUri = await generateSpeechWithElevenLabs(text, voiceId);
                } catch { /* preview fetch failed but URL still usable */ }
            }
            if (!dataUri && !speech.audioUrl) {
                throw new Error('No audio data received');
            }
            handleDialogueChange(lineId, {
                clonedAudio: {
                    name: `dialogue-tts-${lineId}-${Date.now()}.mp3`,
                    data: dataUri || speech.audioUrl,
                    audioUrl: speech.audioUrl || null,   // ← GCS url for video payload
                    voiceId,
                    mimeType: 'audio/mpeg',
                }
            });
            setSpeechGenStatus(prev => ({ ...prev, [lineId]: { status: 'idle', error: null } }));
        } catch(err) {
            const msg = err instanceof Error ? err.message : "Speech generation failed";
            setSpeechGenStatus(prev => ({ ...prev, [lineId]: { status: 'error', error: msg } }));
        }
    };

    // Feb 2026 — Auto-generate TTS for any dialogue line whose character
    // has a voice assigned AND whose text has changed since the last attempt.
    // This runs as pre-production is being prepared so audio_url is ready
    // by the time the user hits "Generate Video".
    // Skipped in ASMR mode.
    //
    // IMPORTANT: TTS requests are serialized (one-at-a-time) — ElevenLabs
    // caps concurrent requests per plan (~2 for starter tiers), so firing
    // them all via Promise.all produced 429 Too Many Requests once ≥3 lines
    // needed audio. The queue below awaits each request before starting the
    // next; generateSpeech itself also retries 429s with exponential
    // backoff for graceful recovery.
    useEffect(() => {
        const audioStyle = projectData?.audioStyle || 'both';
        if (audioStyle === 'asmr') return;
        const allChars = [...(projectData?.characters || []), ...(characters || [])];
        const queue = [];
        for (const [lineId, D] of Object.entries(dialogues || {})) {
            const text = String(D?.text || '').trim();
            if (!text) continue;
            const char = allChars.find(c => c.id === D.characterId);
            const voiceId = char?.elevenLabsVoiceId || char?.voiceId;
            if (!voiceId) continue;
            if (D?.clonedAudio?.audioUrl) continue; // already generated
            const key = `${text}|${D.characterId}|${voiceId}`;
            if (autoTtsAttempted.current.get(lineId) === key) continue; // already tried this exact combo
            const currentStatus = speechGenStatus?.[lineId]?.status;
            if (currentStatus === 'generating') continue;
            autoTtsAttempted.current.set(lineId, key);
            queue.push({ lineId, text });
        }
        if (queue.length > 0) {
            console.log(`🎙️ Auto-TTS: queued ${queue.length} voice generation${queue.length === 1 ? '' : 's'} (sequential)…`);
            // Fire-and-forget sequential runner. Per-line errors are captured
            // in speechGenStatus by handleTriggerGenerateSpeech itself.
            (async () => {
                for (const { lineId, text } of queue) {
                    try {
                        await handleTriggerGenerateSpeech(lineId, text);
                    } catch { /* per-line error already recorded */ }
                }
            })();
        }
        // handleTriggerGenerateSpeech is a stable closure; adding it as a dep
        // retriggers the effect on every render.
    }, [dialogues, projectData?.characters, projectData?.audioStyle, characters]);


    
    const handleAddTextOverlay = () => {
        const newOverlay = {
            id: uuidv4(),
            content: 'Your text here',
            aiStyle: true,
            fontFamily: FONT_OPTIONS[0],
            fontSize: 48,
            color: '#FFFFFF'
        };
        setTextOverlays(prev => [...prev, newOverlay]);
    };

    const handleUpdateTextOverlay = (id, updates) => {
        setTextOverlays(prev => prev.map(o => o.id === id ? { ...o, ...updates } : o));
    };

    const handleRemoveTextOverlay = (id) => {
        setTextOverlays(prev => prev.filter(o => o.id !== id));
    };

    const constructFinalPrompt = () => {
        const cinematicActions = timedActions
            .map(a => `@[${a.startTime}-${a.endTime}] ${a.action?.trim()}`)
            .join(' ');
            
        let finalPrompt = `[${cameraShot}] ${cinematicActions}`;
        const audioStyle = projectData?.audioStyle || 'both';

        if (audioStyle === 'asmr') {
            // ASMR mode: strip dialogue/narration/SFX/VFX/BGM/ambient. Emit a
            // single [ASMR] cue so the video model renders ONLY ASMR audio.
            const asmrCue = (ambient && ambient.trim())
                || 'Close-mic ASMR: crisp, intimate textures that match the on-screen action. No voices, no music, no traditional SFX.';
            finalPrompt += ` [ASMR]: ${asmrCue}.`;
        } else {
            // Sort all lines by start time so the prompt mirrors the script's
            // chronological order — including multiple lines from the same
            // character / narrator.
            const linesSorted = Object.values(dialogues)
                .filter(D => typeof D.text === 'string' && D.text?.trim() !== '')
                .sort((a, b) => (parseFloat(a.startTimecode) || 0) - (parseFloat(b.startTimecode) || 0));

            const dialogueEntries = linesSorted
                .filter(D => projectData.characters.find(c => c.id === D.characterId)?.category !== 'narrator')
                .map(D => {
                    const charName = projectData.characters.find(c => c.id === D.characterId)?.name || 'Character';
                    return `@[${D.startTimecode}-${D.endTimecode}] ${charName}: "${D.text?.trim() || ''}"`;
                });

            if (dialogueEntries.length > 0) {
                finalPrompt += ` [Dialogue]: ${dialogueEntries.join(' ')}.`;
            }

            const narrationEntries = linesSorted
                .filter(D => projectData.characters.find(c => c.id === D.characterId)?.category === 'narrator')
                .map(D => `@[${D.startTimecode}-${D.endTimecode}] "${D.text?.trim() || ''}"`);

            if (narrationEntries.length > 0) {
                finalPrompt += ` [Narration]: ${narrationEntries.join(' ')}.`;
            }

            if (sfx.trim()) {
                finalPrompt += ` [SFX]: ${sfx.trim()}.`;
            }
            if (vfx.trim()) {
                finalPrompt += ` [VFX]: ${vfx.trim()}.`;
            }
            if (bgm.trim()) {
                finalPrompt += ` [BGM]: ${bgm.trim()}.`;
            }
            if (ambient.trim()) {
                finalPrompt += ` [Ambient]: ${ambient.trim()}.`;
            }
        }
        
        const textOverlayEntries = textOverlays.map(overlay => {
            if (!overlay.content?.trim()) return '';
            if (overlay.aiStyle) {
                return `Add text overlay: "${overlay.content}". Use a cinematic style that fits the scene.`;
            } else {
                return `Add text overlay: "${overlay.content}". Font: ${overlay.fontFamily}, Size: ${overlay.fontSize}px, Color: ${overlay.color}.`;
            }
        }).filter(Boolean);

        if (textOverlayEntries.length > 0) {
            finalPrompt += ` [Text Overlays]: ${textOverlayEntries.join(' ')}`;
        }
        
        return finalPrompt;
    };

    const attemptVideoGeneration = async (modelId, finalPrompt) => {
        const modelName = availableVideoModels.find(m => m.id === modelId)?.name || modelId;
        setLoadingMessage(`Generating with ${modelName}...`);
        
        const audioStyle = projectData?.audioStyle || 'both';

        // Prepare structured dialogues data for proper voice/character mapping.
        // In ASMR mode, send an empty array — no character speech should be
        // generated; the [ASMR] cue in the finalPrompt is all the video model
        // needs. One entry per LINE (multiple lines per character allowed).
        //
        // Feb 2026 — When the line has a `clonedAudio.audioUrl` (auto-TTS
        // populated by the useEffect above), include it as `audio_url` so
        // the backend can splice the actual voice track into the video
        // instead of relying on the model to synthesise from `text`.
        // Feb 2026 — Narrator lines are ALSO forwarded here so their
        // pre-generated TTS `audio_url` reaches the video model as an audio
        // reference (same shape as character dialogue audio references).
        // The `[Narration]: "…"` line in the final prompt is kept for the
        // model's textual context; this payload adds the audible track.
        const dialoguesData = audioStyle === 'asmr' ? [] : Object.values(dialogues)
            .filter(D => {
                const charFromProject = projectData.characters.find(c => c.id === D.characterId);
                const charFromProp = characters.find(c => c.id === D.characterId);
                const char = charFromProject || charFromProp;

                const hasText = typeof D.text === 'string' && D.text?.trim() !== '';

                return char && hasText;
            })
            .map(D => {
                const charFromProject = projectData.characters.find(c => c.id === D.characterId);
                const charFromProp = characters.find(c => c.id === D.characterId);
                const char = charFromProject || charFromProp;
                const audioUrl = D.clonedAudio?.audioUrl || null;
                return {
                    character_id: D.characterId,
                    character_name: char?.name || 'Character',
                    start_time: D.startTimecode,
                    end_time: D.endTimecode,
                    text: D.text?.trim() || '',
                    order: D.order || 0,
                    character_image: char?.image || null,
                    voice_id: char?.elevenLabsVoiceId || char?.voiceId || null,
                    audio_url: audioUrl,
                };
            })
            .sort((a, b) => {
                const aTime = parseFloat(a.start_time) || 0;
                const bTime = parseFloat(b.start_time) || 0;
                return aTime - bTime;
            });
        
        // Prepare cinematic actions with timecodes
        const cinematicActions = timedActions
            .filter(a => a.action?.trim())
            .map(a => ({
                start_time: a.startTime,
                end_time: a.endTime,
                action: a.action?.trim(),
                character: a.character || null,
                emotion: a.emotion || null
            }));
        
        console.log('🎭 Dialogues being sent to backend:', dialoguesData);
        console.log('🎬 Cinematic actions being sent:', cinematicActions);
        
        // Use the unified AI service for all models
        // Pass the model ID to the backend which will route to the correct provider
        const { generateVideo } = await import('../services/aiService');

        // Refs-to-video mode (Feb 2026, panel-less variant): toggled by the
        // PRESENCE OF ANY REFERENCE IMAGE — character or element — not panel
        // presence. A shot with 0 panels but with characters/elements still
        // generates a video (user requirement). Panel images only act as a
        // legacy fallback when the shot has no characters/elements.
        const panelImagesInOrder = Array.isArray(shot?.panels)
            ? shot.panels.map(p => p?.image).filter(Boolean)
            : [];

        // Collect visual references: character portraits + element photos.
        // Dedup on URL so a character that recurs across panels is sent once.
        const elementsInShot = (projectData?.characters || []).filter(
            c => c.category === 'element' && Array.isArray(shot?.elementIds) && shot.elementIds.includes(c.id)
        );
        const characterElementRefs = (() => {
            const seen = new Set();
            const out = [];
            for (const ch of (Array.isArray(characters) ? characters : [])) {
                if (ch?.image && !seen.has(ch.image)) { seen.add(ch.image); out.push(ch.image); }
            }
            for (const el of elementsInShot) {
                if (el?.image && !seen.has(el.image)) { seen.add(el.image); out.push(el.image); }
            }
            return out;
        })();
        const effectiveRefs = characterElementRefs.length > 0
            ? characterElementRefs
            : panelImagesInOrder;
        const isReferencesToVideo = effectiveRefs.length > 0;
        const referenceImagesForBackend = isReferencesToVideo ? effectiveRefs : null;

        // Feb 2026 — BytePlus ModelArk (Seedance) pre-approved actor assets.
        // Collected from characters that ship with a pre-approved asset
        // (admin backend attaches these on the Actors Library). generateVideo
        // only forwards them when the resolved model is a seedance variant.
        //
        // Lazy backfill (Feb 2026): projects created BEFORE the Cast & World
        // capture fix shipped have characters with `actorId` but no
        // `byteplusAssetGroupId`. Rather than force the user to re-pick each
        // actor, we fetch the actor list from the admin backend once per
        // generation attempt and attach the refs on the fly. The patched
        // characters are also persisted via `onProjectUpdate` (if provided)
        // so subsequent generations don't repeat the fetch.
        const shotCharsForBackfill = Array.isArray(characters) ? characters : [];
        const projChars = Array.isArray(projectData?.characters) ? projectData.characters : [];
        const needsBackfill = [...shotCharsForBackfill, ...projChars].some(
            c => c && c.actorId && !(c.byteplusAssetGroupId && c.byteplusAssetId)
        );

        let effectiveShotChars = shotCharsForBackfill;
        let effectiveProjChars = projChars;
        if (needsBackfill) {
            console.log('🔎 BytePlus asset backfill — some characters have actorId but no refs');
            const { patchedCharacters: patchedProj, patchedIds } = await backfillByteplusForCharacters(
                projChars,
                { logger: (m) => console.log(`   ${m}`) }
            );
            if (patchedIds.size > 0) {
                effectiveProjChars = patchedProj;
                // Keep the shot-local list in sync with the project list so the
                // dedup pass below sees the refs on both sides.
                effectiveShotChars = shotCharsForBackfill.map(sc => {
                    if (!sc || !patchedIds.has(sc.id)) return sc;
                    const patched = patchedProj.find(p => p.id === sc.id);
                    return patched ? { ...sc, ...patched } : sc;
                });
                // Persist the patched refs back to the project so we don't
                // refetch on every generation attempt.
                if (typeof onUpdateCharacter === 'function') {
                    try {
                        for (const id of patchedIds) {
                            const patched = patchedProj.find(p => p.id === id);
                            if (patched) onUpdateCharacter(patched);
                        }
                    } catch (e) {
                        console.warn('⚠️  Failed to persist backfilled byteplus refs:', e.message);
                    }
                }
            }
        }

        // Linked to the exact photo URL in reference_images; approved assets only.
        const byteplusAssets = buildByteplusAssetsPayload(effectiveProjChars, effectiveShotChars, referenceImagesForBackend || []);
        const productReferences = buildProductReferencesPayload(elementsInShot, referenceImagesForBackend || []);
        console.log(`🧾 BytePlus assets linked: ${byteplusAssets.map(b => `${b.character_name}→${b.image_url}`).join(', ') || 'none'}`);

        // Feb 2026 — explicit capability tag so admin backend routing is
        // not inferred from the model_key slug. Mirrors the branch the
        // frontend already uses to choose the model dropdown (isPanelBased
        // → refs-to-video; hasBothFrames → start-end-frame; etc).
        const capabilityForBackend = isReferencesToVideo
            ? 'references-to-video'
            : (hasBothFrames
                ? 'start-end-frame'
                : (hasOnlyOneFrame
                    ? 'image-to-video'
                    : 'text-to-video'));

        // Feb 2026 — per-character costume references (secondary visual
        // reference for refs-to-video models). Only emitted for shot
        // characters that have at least one costume URL on them. Each entry
        // is structured per-character so the backend cannot mis-map a
        // costume to the wrong character.
        const characterCostumesForBackend = await ensureHostedCostumeUrls((() => {
            const seen = new Set();
            const out = [];
            for (const ch of (Array.isArray(characters) ? characters : [])) {
                if (!ch || !ch.id || seen.has(ch.id)) continue;
                seen.add(ch.id);
                const costumeUrl = getCharacterCostumeForVideo(ch);
                if (!costumeUrl) continue;
                out.push({
                    character_id: ch.id,
                    character_name: ch.name || 'Character',
                    costume_image_url: costumeUrl,
                });
            }
            return out;
        })());

        const audioReferencesForBackend = buildAudioReferencesPayload(audioReferences);
        const referenceVideosForBackend = buildReferenceVideosPayload(referenceVideos);
        if (referenceVideosForBackend.length) console.log('🎞️ reference_videos:', referenceVideosForBackend);

        const result = await generateVideo(
            finalPrompt,
            duration,
            modelId,  // Pass the model ID/key to backend
            projectData.aspectRatio,
            isReferencesToVideo ? null : (shot.startFrameImage || null),
            isReferencesToVideo ? null : (shot.endFrameImage || null),
            setLoadingMessage,
            dialoguesData,      // Pass structured dialogues
            cinematicActions,   // Pass cinematic actions
            referenceImagesForBackend,
            byteplusAssets.length > 0 ? byteplusAssets : null,
            capabilityForBackend,
            characterCostumesForBackend.length > 0 ? characterCostumesForBackend : null,
            resolution || null,
            audioReferencesForBackend.length > 0 ? audioReferencesForBackend : null,
            productReferences.length > 0 ? productReferences : null,
            referenceVideosForBackend.length > 0 ? referenceVideosForBackend : null
        );
        
        if (!result.videoUrl) {
            throw new Error('No video URL returned from backend');
        }
        
        return result; // Return full result including creditsUsed
    };

    const handleGenerateVideo = async () => {
        if (timedActions.every(a => !a.action || !a.action.trim())) {
            setError("Prompt cannot be empty.");
            return;
        }

        // Don't deduct upfront - backend handles credit deduction
        // if (onDeductCredits && !onDeductCredits(CREDIT_COSTS.VIDEO_GENERATION)) return;

        setGeneratedVideoUrl(null);
        setIsGeneratingVideo(true);
        setError(null);
        try {
            const finalPrompt = constructFinalPrompt();
            const result = await attemptVideoGeneration(videoModel, finalPrompt);
            let videoUrl = result.videoUrl;
            
            // Auto voice-change removed — user can apply voice change manually via Video Editor's Voice Changer tool

            setGeneratedVideoUrl(videoUrl);
            
            // Track credits used from backend response (with remaining credits and job ID)
            if (onTrackCreditsUsed && result.creditsUsed) {
                onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
            }
        } catch (err) {
            console.error(err);
            let errorMessage = err instanceof Error ? err.message : 'An unknown error occurred during video generation.';
            
            // Handle insufficient credits error
            if (errorMessage.includes('INSUFFICIENT_CREDITS') || errorMessage.includes('Not enough credits')) {
                errorMessage = 'Not enough credits. Please purchase more credits to continue.';
            }
            
            setError(errorMessage);
        } finally {
            setIsGeneratingVideo(false);
            setLoadingMessage('');
        }
    };
    
    const handleSaveToProject = async () => {
        setIsSaving(true);
        setError(null);

        // Each LINE that has cloned audio is persisted as its own audio entry —
        // multiple lines per character are kept separate (their own timing).
        const dialogueAudios = Object.entries(dialogues)
            .filter(([, d]) => d.clonedAudio)
            .filter(([, d]) => projectData.characters.find(c => c.id === d.characterId)?.category !== 'narrator')
            .map(([lineId, d]) => ({
                lineId,
                characterId: d.characterId,
                text: d.text,
                startTime: d.startTimecode,
                endTime: d.endTimecode,
                audioData: d.clonedAudio,
            }));

        // Narrator: collect ALL narrator lines with cloned audio. Preserve the
        // legacy `narrationAudio` (single first entry) for back-compat AND emit
        // a `narrationAudios` array so downstream can stitch by timecode.
        const narrationLines = narrator
            ? Object.entries(dialogues)
                .filter(([, d]) => d.characterId === narrator.id && d.clonedAudio)
                .map(([lineId, d]) => ({
                    lineId,
                    characterId: narrator.id,
                    text: d.text,
                    startTime: d.startTimecode,
                    endTime: d.endTimecode,
                    audioData: d.clonedAudio,
                }))
            : [];
        const narrationAudioPayload = narrationLines.length > 0
            ? { ...narrationLines[0], lines: narrationLines }
            : undefined;

        // Persist edited dialogue lines back to `shot.dialogues` so future
        // batch generations (Generate All Scene/Project Videos) use the
        // user's edits — including multiple lines per character.
        const parseSeconds = (v) => {
            if (v === undefined || v === null) return 0;
            if (typeof v === 'number') return v;
            const m = String(v).match(/[\d.]+/);
            return m ? parseFloat(m[0]) : 0;
        };
        const persistedDialogues = Object.values(dialogues)
            .map(d => {
                const ch = projectData.characters?.find(c => c.id === d.characterId)
                    || characters?.find(c => c.id === d.characterId);
                return {
                    characterId: d.characterId,
                    characterName: ch?.name || 'Character',
                    text: (d.text || '').trim(),
                    startTime: parseSeconds(d.startTimecode),
                    endTime: parseSeconds(d.endTimecode),
                    order: d.order ?? 0,
                    // Feb 2026 — carry the GCS audio URL so batch generation
                    // ("Generate All Scene Videos") can use pre-generated TTS.
                    audioUrl: d.clonedAudio?.audioUrl || null,
                    voiceId: ch?.elevenLabsVoiceId || ch?.voiceId || null,
                };
            })
            .filter(d => d.text !== '')
            .sort((a, b) => a.startTime - b.startTime);

        // Get the actual video duration for use in the timeline
        let actualVideoDuration = duration; // Default to requested duration
        if (generatedVideoUrl) {
            try {
                actualVideoDuration = await getActualVideoDuration(generatedVideoUrl);
                console.log('📹 Actual video duration:', actualVideoDuration, 'seconds');
            } catch (e) {
                console.warn('⚠️ Could not get video duration, using requested duration:', duration);
            }
        }

        onSave(shot.id, {
            videoUrl: generatedVideoUrl,
            videoDuration: actualVideoDuration, // Store the actual video duration
            dialogueAudios: dialogueAudios.length > 0 ? dialogueAudios : undefined,
            narrationAudio: narrationAudioPayload,
            textOverlays: textOverlays.length > 0 ? textOverlays : undefined,
            dialogues: persistedDialogues,
            audioReferences
        });
        setIsSaving(false);
    };
    
    // Helper function to get actual video duration
    const getActualVideoDuration = (videoUrl) => {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.crossOrigin = 'anonymous';
            
            let resolved = false;
            
            const cleanup = () => {
                video.onloadedmetadata = null;
                video.ondurationchange = null;
                video.onerror = null;
            };
            
            video.onloadedmetadata = () => {
                if (!resolved && video.duration && !isNaN(video.duration) && video.duration !== Infinity && video.duration > 0) {
                    resolved = true;
                    cleanup();
                    resolve(video.duration);
                }
            };
            
            video.ondurationchange = () => {
                if (!resolved && video.duration && !isNaN(video.duration) && video.duration !== Infinity && video.duration > 0) {
                    resolved = true;
                    cleanup();
                    resolve(video.duration);
                }
            };
            
            video.onerror = () => {
                if (!resolved) {
                    resolved = true;
                    cleanup();
                    reject(new Error('Video load error'));
                }
            };
            
            setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    cleanup();
                    reject(new Error('Video duration timeout'));
                }
            }, 5000);
            
            video.src = videoUrl;
            video.load();
        });
    };
    
    const handleDownload = async () => {
        if (!generatedVideoUrl) return;
        setError(null);
        try {
            const response = await fetch(generatedVideoUrl);
            if (!response.ok) throw new Error('Failed to fetch video data for download.');
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = `loopai_shot_${shot.id}.mp4`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            URL.revokeObjectURL(objectUrl);
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : "An unknown error occurred during download.";
            console.error("Download failed:", e);
            setError(`Video download failed. ${errorMessage}`);
        }
    };
    
    const renderMainContent = () => {
        if (isGeneratingVideo || isSaving) {
            return (
                <div className="text-center">
                    <LoadingSpinner className="w-12 h-12 mx-auto mb-4 animate-spin" />
                    <h3 className="text-2xl font-semibold text-gray-300 mb-2">{isSaving ? 'Saving...' : 'Creating Magic...'}</h3>
                    <p className="text-lg text-purple-400">{loadingMessage}</p>
                </div>
            );
        }

        if (generatedVideoUrl) {
            return (
                 <video
                    key={generatedVideoUrl}
                    src={generatedVideoUrl}
                    controls
                    autoPlay
                    loop
                    className="w-full h-full object-contain"
                >
                    Your browser does not support the video tag.
                </video>
            )
        }

        // Panel-based shot? render the ordered strip of panel images instead
        // of the legacy Start Frame / End Frame two-card preview.
        const panelsInOrder = Array.isArray(shot?.panels)
            ? shot.panels.filter(p => !!p?.image)
            : [];

        if (panelsInOrder.length > 0) {
            return (
                <div className="w-full">
                    <div className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-2">
                        Reference images <span className="text-zinc-500 font-normal normal-case">— {panelsInOrder.length} panel{panelsInOrder.length === 1 ? '' : 's'}, sent in order to references-to-video</span>
                    </div>
                    <div className="flex gap-3 overflow-x-auto pb-2 panel-strip-scroller">
                        {panelsInOrder.map((p, i) => (
                            <div
                                key={p.id || i}
                                className="shrink-0 flex-none w-48"
                                data-testid={`prep-panel-${i}`}
                            >
                                <div className="aspect-video bg-black rounded-lg relative overflow-hidden">
                                    <span className="absolute top-2 left-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full z-10">
                                        Panel {i + 1}{p.timecode ? ` · ${p.timecode}` : ''}
                                    </span>
                                    <img
                                        src={p.image}
                                        alt={`Panel ${i + 1}`}
                                        className="w-full h-full object-contain"
                                        crossOrigin="anonymous"
                                        referrerPolicy="no-referrer"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        // Feb 2026 update #3 — no panels but Cast & World ref images exist?
        // Render a character + element strip (same visual treatment as the
        // panel strip) so the user sees exactly what will be sent to the
        // refs-to-video model. Keeps the Shot Pre-Production flow aligned
        // with the Storyboard batch flow (same reference set).
        if (hasCastRefImages) {
            const chars = refEligibleCharacters.filter(c => c.category !== 'element');
            const elems = refEligibleCharacters.filter(c => c.category === 'element');
            return (
                <div className="w-full">
                    <div className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-2">
                        Reference images <span className="text-zinc-500 font-normal normal-case">— {chars.length} character{chars.length === 1 ? '' : 's'}{elems.length > 0 ? ` + ${elems.length} element${elems.length === 1 ? '' : 's'}` : ''}, sent to references-to-video</span>
                    </div>
                    <div className="flex gap-3 overflow-x-auto pb-2 panel-strip-scroller">
                        {[...chars, ...elems].map((c, i) => (
                            <div
                                key={c.id || i}
                                className="shrink-0 flex-none w-48"
                                data-testid={`prep-ref-${c.id}`}
                            >
                                <div className="aspect-video bg-black rounded-lg relative overflow-hidden">
                                    <span className="absolute top-2 left-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full z-10 truncate max-w-[80%]">
                                        {c.category === 'element' ? 'Element' : (c.category === 'narrator' ? 'Narrator' : 'Character')} · {c.name || 'Untitled'}
                                    </span>
                                    <img
                                        src={c.image}
                                        alt={c.name || 'Reference'}
                                        className="w-full h-full object-contain"
                                        crossOrigin="anonymous"
                                        referrerPolicy="no-referrer"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        return (
            <div className="w-full flex items-center justify-center gap-4">
                <div className="w-1/2 aspect-video bg-black rounded-lg flex flex-col items-center justify-center relative">
                    <span className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full z-10">Start Frame</span>
                    {shot.startFrameImage && <img src={shot.startFrameImage} alt="Start frame" className="w-full h-full object-contain rounded-lg" crossOrigin="anonymous" referrerPolicy="no-referrer" />}
                </div>
                <div className="w-1/2 aspect-video bg-black rounded-lg flex flex-col items-center justify-center relative">
                    <span className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full z-10">End Frame</span>
                    {shot.endFrameImage && <img src={shot.endFrameImage} alt="End frame" className="w-full h-full object-contain rounded-lg" crossOrigin="anonymous" referrerPolicy="no-referrer" />}
                </div>
            </div>
        )
    }
    
    const renderFooter = () => {
        const hasContentToSave = generatedVideoUrl || Object.values(dialogues).some(d => d.clonedAudio) || textOverlays.length > 0;
        const isActionEmpty = timedActions.every(a => !a.action || !a.action.trim());

        // A shot can be generated when it has EITHER a legacy startFrameImage,
        // OR panel images, OR (Feb 2026 update #3) at least one character/
        // element in Cast & World with an `image` URL. Without this fix,
        // panel-less + char/element-only shots were still blocked.
        const hasGenerableSource = !!shot.startFrameImage
            || (Array.isArray(shot?.panels) && shot.panels.some(p => !!p?.image))
            || hasCastRefImages;

        const isGenerateDisabled = isGeneratingPrompt ||
                               isGeneratingVideo ||
                               isSaving ||
                               isActionEmpty ||
                               !hasGenerableSource;
                               
        if (generatedVideoUrl) {
            return (
                <>
                    <button onClick={onClose} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                        Close
                    </button>
                    <div className="flex-grow flex justify-end items-center space-x-4">
                        <button 
                            onClick={handleDownload}
                            className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
                        >
                            <DownloadIcon className="w-5 h-5 mr-2" />
                            Download
                        </button>
                        <button 
                            onClick={handleGenerateVideo}
                            disabled={isGenerateDisabled}
                            className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
                        >
                            <MagicWandIcon className="w-5 h-5 mr-2" />
                            Re-generate
                        </button>
                         <button 
                            onClick={handleSaveToProject}
                            disabled={isSaving}
                            className="bg-green-600 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
                        >
                            <SaveIcon className="w-5 h-5 mr-2" />
                            Save to Project
                        </button>
                    </div>
                </>
            );
        }

        return (
            <>
                <button onClick={onClose} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                    Cancel
                </button>
                {hasContentToSave && (
                     <button 
                        onClick={handleSaveToProject}
                        disabled={isSaving}
                        className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
                    >
                        {isSaving ? <LoadingSpinner /> : <SaveIcon className="w-5 h-5 mr-2" />}
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                )}
                <button 
                    onClick={handleGenerateVideo}
                    disabled={isGenerateDisabled}
                    className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
                >
                    {isGeneratingVideo ? <LoadingSpinner /> : <MagicWandIcon className="w-5 h-5 mr-2" />}
                    {isGeneratingVideo ? 'Generating...' : 'Generate Video'}
                </button>
            </>
        )
    }

    return (
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={onClose} role="dialog" aria-modal="true"
        >
            {showPromptEditor && (
                <CinematicPromptEditorModal
                    timedActions={timedActions}
                    onUpdateAction={handleUpdateTimedAction}
                    onRemoveAction={handleRemoveTimedAction}
                    onAddAction={handleAddTimedAction}
                    audioValues={{ sfx, bgm, vfx, ambient }}
                    onAudioChange={(key, val) => ({ sfx: setSfx, bgm: setBgm, vfx: setVfx, ambient: setAmbient })[key](val)}
                    audioReferences={audioReferences}
                    onReferenceChange={handleReferenceChange}
                    onClose={() => setShowPromptEditor(false)}
                    disabled={isGeneratingVideo}
                />
            )}
            {showVoiceModal && (
                <ElevenLabsVoiceModal
                    onClose={() => setShowVoiceModal(false)}
                    onSelect={handleVoiceSelect}
                />
            )}
            <div
                className="bg-[#181820] border border-gray-700 rounded-2xl w-full max-w-6xl h-[92vh] flex flex-col overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                <header className="p-4 flex justify-between items-center border-b border-gray-700 shrink-0">
                    <h2 className="text-xl font-bold">Shot Pre-Production</h2>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-700"><CloseIcon className="w-6 h-6" /></button>
                </header>

                <div className="flex-grow min-h-0 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden">
                    <main className="flex-1 min-w-0 flex flex-col items-center p-4 bg-black/30 md:overflow-y-auto" data-testid="preprod-main">
                        <div className="w-full my-auto">{renderMainContent()}</div>
                        {!generatedVideoUrl && (
                            <ReferenceVideosPanel videos={referenceVideos} setVideos={setReferenceVideos} disabled={isGeneratingVideo} />
                        )}
                    </main>

                    <aside className="w-full md:w-[340px] lg:w-[400px] min-w-0 bg-[#101018] flex flex-col md:overflow-y-auto shrink-0" data-testid="preprod-sidebar">
                        <div className="flex border-b border-gray-700 shrink-0">
                            <TabButton name="Prompt" tabId="prompt" activeTab={activeTab} setActiveTab={setActiveTab} />
                            {characters.length > 0 && <TabButton name="Dialogue" tabId="dialogue" activeTab={activeTab} setActiveTab={setActiveTab} />}
                            {narrator && <TabButton name="Narration" tabId="narration" activeTab={activeTab} setActiveTab={setActiveTab} />}
                            <TabButton name="Text" tabId="text" activeTab={activeTab} setActiveTab={setActiveTab} />
                        </div>
                        <div className="p-3 space-y-3 overflow-y-auto">
                            {error && <div className="bg-red-500/20 border border-red-500 text-red-300 p-3 rounded-lg text-sm">{error}</div>}
                            
                            {activeTab === 'prompt' && (
                                <>
                                    <div className="border border-gray-700 rounded-lg p-3">
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-sm font-semibold text-purple-300">Cinematic Prompt</label>
                                            <button type="button" onClick={() => setShowPromptEditor(true)} disabled={isGeneratingPrompt}
                                                className="text-xs px-2 py-1 rounded-md bg-purple-600/40 hover:bg-purple-600 text-white font-semibold disabled:opacity-50 transition-colors"
                                                data-testid="open-cinematic-prompt-editor">Expand editor</button>
                                        </div>
                                        {isGeneratingPrompt ? (
                                            <div className="w-full h-24 p-2 bg-gray-700/60 rounded-md flex items-center justify-center">
                                                <LoadingSpinner className="w-6 h-6 mr-2" />
                                                <span>Generating prompt...</span>
                                            </div>
                                        ) : (() => {
                                            // Group rows by source. `source: 'scene'` is set by
                                            // `flattenSceneActions` for rows derived from the
                                            // admin's `scene_actions[]` field. Everything else
                                            // (character_actions, main_prompt fallback, locally
                                            // added rows) is treated as a character/default row.
                                            // Section headers only appear once scene rows exist,
                                            // so the UI is unchanged when scene_actions are absent.
                                            const charRows = timedActions.filter(a => a.source !== 'scene');
                                            const sceneRows = timedActions.filter(a => a.source === 'scene');
                                            const showSections = sceneRows.length > 0;

                                            const renderRow = (action, index) => (
                                                <div key={action.id} className="flex items-start gap-2">
                                                    <div className="flex items-center gap-1">
                                                        <input type="text" value={action.startTime} onChange={e => handleUpdateTimedAction(action.id, { startTime: e.target.value })} className="w-16 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm" placeholder="0.0s" />
                                                        <span className="text-gray-500">-</span>
                                                        <input type="text" value={action.endTime} onChange={e => handleUpdateTimedAction(action.id, { endTime: e.target.value })} className="w-16 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm" placeholder="4.0s" />
                                                    </div>
                                                    <textarea value={action.action} onChange={e => handleUpdateTimedAction(action.id, { action: e.target.value })} rows={2} className="flex-grow min-w-0 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm resize-none" placeholder={`Action ${index + 1}...`} />
                                                    <button onClick={() => handleRemoveTimedAction(action.id)} className="p-2 text-red-400 hover:bg-red-500/20 rounded-full flex-shrink-0 self-center"><TrashIcon className="w-4 h-4" /></button>
                                                </div>
                                            );

                                            if (!showSections) {
                                                // Legacy single-section render — preserves the
                                                // existing UX when no scene_actions exist.
                                                return (
                                                    <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                                                        {timedActions.map(renderRow)}
                                                        <button onClick={handleAddTimedAction} className="w-full text-xs bg-indigo-600/50 hover:bg-indigo-600 text-white font-semibold py-1 rounded-md flex items-center justify-center"><PlusIcon className="w-3 h-3 mr-1" /> Add Action</button>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div className="space-y-3 max-h-72 overflow-y-auto pr-2">
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[11px] font-semibold uppercase tracking-wider text-indigo-300">Character Actions</span>
                                                            <span className="text-[10px] text-gray-500">{charRows.length} row{charRows.length === 1 ? '' : 's'}</span>
                                                        </div>
                                                        {charRows.length === 0
                                                            ? <p className="text-xs text-gray-500 italic">No character actions for this shot.</p>
                                                            : charRows.map(renderRow)}
                                                        <button onClick={handleAddTimedAction} className="w-full text-xs bg-indigo-600/50 hover:bg-indigo-600 text-white font-semibold py-1 rounded-md flex items-center justify-center"><PlusIcon className="w-3 h-3 mr-1" /> Add Character Action</button>
                                                    </div>
                                                    <div className="space-y-2 pt-2 border-t border-gray-700/60">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-300">Scene Actions</span>
                                                            <span className="text-[10px] text-gray-500">{sceneRows.length} row{sceneRows.length === 1 ? '' : 's'} · product / landscape / animal / VFX</span>
                                                        </div>
                                                        {sceneRows.map(renderRow)}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                    <div className="border border-gray-700 rounded-lg p-3 space-y-3">
                                        <label className="text-sm font-semibold block text-purple-300">Video Settings</label>
                                        <div>
                                            <label className="text-xs font-medium text-gray-400">Model</label>
                                            {isLoadingModels ? (
                                                <div className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm flex items-center">
                                                    <LoadingSpinner className="w-4 h-4 mr-2" />
                                                    Loading models...
                                                </div>
                                            ) : (
                                                <select value={videoModel} onChange={e => setVideoModel(e.target.value)} className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" disabled={isGeneratingVideo} data-testid="video-model-select">
                                                    {availableVideoModels.map(model => (
                                                        <option key={model.id} value={model.id} disabled={model.disabled}>
                                                            {model.name}{model.provider ? ` (${model.provider})` : ''}{model.costEstimate ? ` - ${model.costEstimate}` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                        {/* Feb 2026 — Resolution dropdown moved OUT of the per-model
                                            Duration/Resolution block so it ALWAYS shows for any model
                                            the admin catalog returns (seedanceB2.5-ref2video etc.).
                                            Options are derived from the model's `resolutions` field
                                            (admin catalog) or the provider fallback map. */}
                                        <div>
                                            <label className="text-xs font-medium text-gray-400">Resolution</label>
                                            <select
                                                value={resolution}
                                                onChange={(e) => setResolution(e.target.value)}
                                                disabled={isGeneratingVideo || resolutionOptions.length <= 1}
                                                className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-sm text-white disabled:opacity-60 mt-1"
                                                data-testid="video-resolution-select"
                                            >
                                                {resolutionOptions.map(res => (
                                                    <option key={res} value={res}>{res}{resolutionCredits[res] != null ? ` · ${resolutionCredits[res]} credits/s` : ''}</option>
                                                ))}
                                            </select>
                                            {resolutionOptions.length <= 1 && (
                                                <p className="text-[10px] text-gray-500 mt-1">This model supports only one resolution.</p>
                                            )}
                                        </div>
                                        {durationProfile && (
                                            <div className="mt-3" data-testid="video-duration-profile-control">
                                                <label className="text-xs font-medium text-gray-400" data-testid="video-duration-label">Duration: {duration}s</label>
                                                <input
                                                    type="range"
                                                    min={durationProfile.minSeconds}
                                                    max={durationProfile.maxSeconds}
                                                    step="1"
                                                    value={duration}
                                                    onChange={e => setDuration(parseInt(e.target.value, 10))}
                                                    className="w-full mt-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                                                    disabled={isGeneratingVideo}
                                                    data-testid="video-duration-slider"
                                                />
                                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                                    <span>{durationProfile.minSeconds}s</span>
                                                    <span>Default {durationProfile.defaultSeconds}s</span>
                                                    <span>{durationProfile.maxSeconds}s</span>
                                                </div>
                                            </div>
                                        )}
                                        {(videoModel === 'wan-2.2' || videoModel === 'kling-2.6' || videoModel === 'kling-2.5-turbo' || videoModel === 'sora-2' || videoModel === 'ltx-fast' || videoModel === 'minimax') && (
                                            <div className="space-y-3 mt-3">
                                                <div>
                                                    <label className="text-xs font-medium text-gray-400">Duration: {duration}s</label>
                                                    {videoModel === 'wan-2.2' ? (
                                                        <div className="flex gap-2 mt-1">
                                                            {[5].map(d => (
                                                                <button key={d} type="button" onClick={() => setDuration(d)} disabled={isGeneratingVideo} className={`flex-1 py-1 text-xs rounded-md font-semibold transition-colors ${duration === d ? 'bg-purple-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}>{d}s (Fixed)</button>
                                                            ))}
                                                        </div>
                                                    ) : videoModel === 'sora-2' ? (
                                                        <div className="flex gap-2 mt-1">
                                                            {[4, 8, 12].map(d => (
                                                                <button key={d} type="button" onClick={() => setDuration(d)} disabled={isGeneratingVideo} className={`flex-1 py-1 text-xs rounded-md font-semibold transition-colors ${duration === d ? 'bg-purple-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}>{d}s</button>
                                                            ))}
                                                        </div>
                                                    ) : videoModel === 'ltx-fast' ? (
                                                        <div className="flex gap-2 mt-1">
                                                            {[5, 8, 10].map(d => (
                                                                <button key={d} type="button" onClick={() => setDuration(d)} disabled={isGeneratingVideo} className={`flex-1 py-1 text-xs rounded-md font-semibold transition-colors ${duration === d ? 'bg-purple-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}>{d}s</button>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <input
                                                                type="range"
                                                                min="5" max="10" step="5"
                                                                value={duration}
                                                                onChange={e => setDuration(parseInt(e.target.value, 10))}
                                                                className="w-full mt-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                                                                disabled={isGeneratingVideo}
                                                            />
                                                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                                                                <span>5s</span>
                                                                <span>10s</span>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        <div>
                                            <label className="text-xs font-medium text-gray-400">Camera Movement</label>
                                            <select value={cameraShot} onChange={e => setCameraShot(e.target.value)} className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" disabled={isGeneratingVideo}>
                                                {CAMERA_SHOTS.map(shot => <option key={shot} value={shot}>{shot}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-400">SFX (Sound Effects)</label>
                                            {audioReferences['sfx']?.url && <button type="button" onClick={() => setShowPromptEditor(true)} className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-600/30 text-emerald-200" data-testid="ref-chip-sfx">{audioReferences['sfx'].type === 'video' ? 'Video' : 'Audio'} ref attached</button>}
                                            <textarea
                                                value={sfx}
                                                onChange={e => setSfx(e.target.value)}
                                                placeholder="e.g., thunder, footsteps, door creaking..."
                                                rows={2}
                                                className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm resize-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-400">VFX (Visual Effects)</label>
                                            {audioReferences['vfx']?.url && <button type="button" onClick={() => setShowPromptEditor(true)} className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-600/30 text-emerald-200" data-testid="ref-chip-vfx">{audioReferences['vfx'].type === 'video' ? 'Video' : 'Audio'} ref attached</button>}
                                            <textarea
                                                value={vfx}
                                                onChange={e => setVfx(e.target.value)}
                                                placeholder="e.g., explosions, sparkles, lens flare..."
                                                rows={2}
                                                className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm resize-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-400">🎵 BGM (Background Music)</label>
                                            {audioReferences['bgm']?.url && <button type="button" onClick={() => setShowPromptEditor(true)} className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-600/30 text-emerald-200" data-testid="ref-chip-bgm">{audioReferences['bgm'].type === 'video' ? 'Video' : 'Audio'} ref attached</button>}
                                            <textarea
                                                value={bgm}
                                                onChange={e => setBgm(e.target.value)}
                                                placeholder="e.g., Gentle acoustic guitar, uplifting melody..."
                                                rows={2}
                                                className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm resize-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-400">🌿 Ambient Sound</label>
                                            {audioReferences['ambient']?.url && <button type="button" onClick={() => setShowPromptEditor(true)} className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-600/30 text-emerald-200" data-testid="ref-chip-ambient">{audioReferences['ambient'].type === 'video' ? 'Video' : 'Audio'} ref attached</button>}
                                            <textarea
                                                value={ambient}
                                                onChange={e => setAmbient(e.target.value)}
                                                placeholder="e.g., City traffic, forest birds, ocean waves..."
                                                rows={2}
                                                className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm resize-none"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                             {activeTab === 'dialogue' && characters.length > 0 && (
                                <div className="border border-gray-700 rounded-lg p-3 space-y-4">
                                    <label className="text-sm font-semibold block text-purple-300">Dialogue & Voice Cloning</label>
                                    {/* Group lines by character, then render each character's lines sorted by start time. */}
                                    {[...characters].map(char => {
                                        const lines = groupDialoguesByCharacter(dialogues, char.id);
                                        const projectChar = projectData.characters.find(c => c.id === char.id) || char;
                                        return (
                                            <div key={char.id} className="bg-gray-800/30 rounded-md p-2 space-y-2" data-testid={`dialogue-char-group-${char.id}`}>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-xs font-bold text-gray-200">{projectChar.name}</span>
                                                    <button
                                                        onClick={() => handleAddDialogueLine(char.id)}
                                                        className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
                                                        data-testid={`add-dialogue-line-${char.id}`}
                                                    >
                                                        <PlusIcon className="w-3 h-3" /> Add line
                                                    </button>
                                                </div>
                                                {lines.length === 0 && (
                                                    <p className="text-xs text-gray-500 italic">No dialogue lines yet — click &quot;Add line&quot; to add one.</p>
                                                )}
                                                {lines.map((line, idx) => (
                                                    <DialogueAudioSection
                                                        key={line.lineId}
                                                        character={projectChar}
                                                        dialogue={dialogues[line.lineId]}
                                                        lineIndex={lines.length > 1 ? idx : undefined}
                                                        onDialogueChange={(update) => handleDialogueChange(line.lineId, update)}
                                                        onGenerateSpeech={() => handleTriggerGenerateSpeech(line.lineId, dialogues[line.lineId].text)}
                                                        generationStatus={speechGenStatus[line.lineId]?.status || 'idle'}
                                                        generationError={speechGenStatus[line.lineId]?.error || null}
                                                        estimatedSpeechSec={estimateSpeechDuration(dialogues[line.lineId]?.text)}
                                                        onDelete={lines.length > 1 ? () => handleRemoveDialogueLine(line.lineId) : undefined}
                                                        onSelectVoice={() => {
                                                            setSelectingVoiceForCharId(char.id);
                                                            setShowVoiceModal(true);
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {activeTab === 'narration' && narrator && (() => {
                                const narratorLines = groupDialoguesByCharacter(dialogues, narrator.id);
                                return (
                                    <div className="border border-gray-700 rounded-lg p-3 space-y-3">
                                        <div className="flex justify-between items-center">
                                            <label className="text-sm font-semibold block text-purple-300">Narration</label>
                                            <button
                                                onClick={() => handleAddDialogueLine(narrator.id)}
                                                className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
                                                data-testid="add-narration-line"
                                            >
                                                <PlusIcon className="w-3 h-3" /> Add line
                                            </button>
                                        </div>
                                        {narratorLines.length === 0 && (
                                            <p className="text-xs text-gray-500 italic">No narration lines yet — click &quot;Add line&quot; to add one.</p>
                                        )}
                                        {narratorLines.map((line, idx) => (
                                            <DialogueAudioSection
                                                key={line.lineId}
                                                character={narrator}
                                                dialogue={dialogues[line.lineId]}
                                                lineIndex={narratorLines.length > 1 ? idx : undefined}
                                                onDialogueChange={(update) => handleDialogueChange(line.lineId, update)}
                                                onGenerateSpeech={() => handleTriggerGenerateSpeech(line.lineId, dialogues[line.lineId]?.text)}
                                                generationStatus={speechGenStatus[line.lineId]?.status || 'idle'}
                                                generationError={speechGenStatus[line.lineId]?.error || null}
                                                estimatedSpeechSec={estimateSpeechDuration(dialogues[line.lineId]?.text)}
                                                onDelete={narratorLines.length > 1 ? () => handleRemoveDialogueLine(line.lineId) : undefined}
                                                onSelectVoice={() => {
                                                    setSelectingVoiceForCharId(narrator.id);
                                                    setShowVoiceModal(true);
                                                }}
                                            />
                                        ))}
                                    </div>
                                );
                            })()}
                            {activeTab === 'text' && (
                                <div className="border border-gray-700 rounded-lg p-3 space-y-3">
                                    <div className="flex justify-between items-center">
                                        <label className="text-sm font-semibold text-purple-300">Text Overlays</label>
                                        <button onClick={handleAddTextOverlay} className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-1 px-2 rounded-md flex items-center">
                                            <PlusIcon className="w-3 h-3 mr-1" /> Add Text
                                        </button>
                                    </div>
                                    {textOverlays.map(overlay => (
                                        <div key={overlay.id} className="bg-gray-800/50 p-3 rounded-md space-y-2">
                                            <div className="flex justify-between items-center">
                                                <input
                                                    type="text"
                                                    value={overlay.content}
                                                    onChange={e => handleUpdateTextOverlay(overlay.id, { content: e.target.value })}
                                                    className="flex-grow p-1 bg-gray-700 border border-gray-600 rounded text-sm"
                                                    placeholder="Text content..."
                                                />
                                                <button onClick={() => handleRemoveTextOverlay(overlay.id)} className="ml-2 p-1 text-red-400 hover:bg-red-500/20 rounded">
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Switch checked={overlay.aiStyle} onChange={(val) => handleUpdateTextOverlay(overlay.id, { aiStyle: val })} />
                                                <span className="text-xs text-gray-400">AI Styling</span>
                                            </div>
                                            {!overlay.aiStyle && (
                                                <div className="grid grid-cols-2 gap-2">
                                                    <select value={overlay.fontFamily} onChange={e => handleUpdateTextOverlay(overlay.id, { fontFamily: e.target.value })} className="p-1 bg-gray-700 border border-gray-600 rounded text-xs">
                                                        {FONT_OPTIONS.map(font => <option key={font} value={font}>{font}</option>)}
                                                    </select>
                                                    <input type="number" value={overlay.fontSize} onChange={e => handleUpdateTextOverlay(overlay.id, { fontSize: parseInt(e.target.value) })} className="p-1 bg-gray-700 border border-gray-600 rounded text-xs" />
                                                    <input type="color" value={overlay.color} onChange={e => handleUpdateTextOverlay(overlay.id, { color: e.target.value })} className="col-span-2 h-8 bg-gray-700 border border-gray-600 rounded" />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </aside>
                </div>

                <footer className="flex flex-wrap gap-2 justify-between items-center p-4 border-t border-gray-700 shrink-0">
                    {renderFooter()}
                </footer>
            </div>
        </div>
    );
};
